import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { ordersApi, paymentApi, authorizeApi, helcimApi, authApi } from '../services/api';
import CardFeeConsentModal from '../components/CardFeeConsentModal';
import { Order, OrderItem, OrderStatus } from '../types';
import { useAuth } from '../context/AuthContext';
import { DealerInvoiceView } from '../components/DealerInvoiceView';
import { ShippingDisclaimer } from '../components/ShippingDisclaimer';

// Label formatting utilities - clean display names
const formatInstallationType = (value: string | undefined): string => {
  if (!value) return 'Wall';
  const map: Record<string, string> = {
    'face_of_header': 'Wall',
    'face of_header': 'Wall',
    'wall': 'Wall',
    'under_header': 'Ceiling',
    'ceiling': 'Ceiling',
    'recessed': 'Wall',
    'recessed_pocket': 'Wall',
  };
  return map[value.toLowerCase()] || value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const formatTrackMount = (value: string | undefined): string => {
  if (!value) return 'Face';
  const map: Record<string, string> = {
    'face': 'Face',
    'inside': 'Inside',
    'inside_face_left': 'Inside/Face Left',
    'inside_face_right': 'Inside/Face Right',
    'inside_outside_left': 'Inside/Outside Left',
    'inside_outside_right': 'Inside/Outside Right',
    'recessed': 'Recessed',
  };
  return map[value.toLowerCase()] || value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const formatUChannel = (value: string | undefined): string => {
  if (!value || value === 'none') return 'None';
  const map: Record<string, string> = {
    'none': 'None',
    'both_sides': 'Both Sides',
    'two_sides': 'Both Sides',
    'left_side': 'Left Side Only',
    'left_side_only': 'Left Side Only',
    'right_side': 'Right Side Only',
    'right_side_only': 'Right Side Only',
    'one_side': 'One Side',
  };
  return map[value.toLowerCase()] || value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const formatShippingMethod = (value: string | undefined): string => {
  if (!value) return 'Not specified';
  const map: Record<string, string> = {
    'willcall': 'Will Call (Pickup)',
    'will_call': 'Will Call (Pickup)',
    'freight': 'Freight Shipping',
    'ltl_freight': 'LTL Freight',
    'ship_to_dealer': 'Ship to My Address',
    'use_customer_address': 'Use Customer Address',
    'different_address': 'Different Address',
  };
  return map[value.toLowerCase()] || value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const formatRemoteType = (value: string | undefined): string => {
  if (!value || value === 'none') return 'None';
  const map: Record<string, string> = {
    'alpha_pioneer_1ch': 'Alpha Pioneer 1-Channel',
    'alpha_pioneer_4ch': 'Alpha Pioneer 4-Channel',
    'alpha_pioneer_8ch_lcd': 'Alpha Pioneer 8-Channel LCD',
    'alpha_pioneer_16ch_lcd': 'Alpha Pioneer 16-Channel LCD',
    'somfy_situo_1ch': 'Somfy Situo 1-Channel',
    'somfy_situo_5ch': 'Somfy Situo 5-Channel',
    'somfy_telis_1ch': 'Somfy Telis 1-Channel',
    'somfy_telis_4ch': 'Somfy Telis 4-Channel',
    'somfy_telis_16ch': 'Somfy Telis 16-Channel',
    'keder_remote_1ch': 'Keder Screens 1-Channel',
    'keder_remote_5ch': 'Keder Screens 5-Channel',
    'keder_remote_15ch': 'Keder Screens 15-Channel',
    'alpha_rts_wall_switch': 'Alpha RTS Wall Switch',
    'somfy_rts_wall_switch': 'Somfy RTS Wall Switch',
    'keder_wall_switch': 'Keder Screens Wall Switch',
  };
  return map[value.toLowerCase()] || value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const formatFabricType = (value: string | undefined): string => {
  if (!value) return '-';
  const map: Record<string, string> = {
    'solar94': 'Solar',
    'solar99': 'Premium Solar',
    'insect': 'Insect',
    'cvw': 'Clear View',
    'omega_pro_3': 'Omega Pro 3%',
    'visiontex_94': 'VisionTex 94',
    'visiontex_99': 'VisionTex 99',
  };
  return map[value.toLowerCase()] || value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const formatBrushSize = (value: string | undefined): string => {
  if (!value) return '1 inch';
  if (value === '1' || value === '1"' || value === '1 inch') return '1 inch';
  if (value === '2' || value === '2"' || value === '2 inch') return '2 inch';
  return value;
};

// Helper to open all installer sheets combined into one printable document
const openAllInstallerSheets = async (orderId: string, unitCount: number) => {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      alert('Please log in to view installer sheets.');
      return;
    }

    // Fetch all installer sheets for all units
    const fetchPromises = [];
    for (let i = 1; i <= unitCount; i++) {
      fetchPromises.push(
        fetch(`/api/bom/installer-sheet/${orderId}/${i}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        })
      );
    }

    const responses = await Promise.all(fetchPromises);

    // Check for auth errors
    if (responses.some(r => r.status === 401)) {
      alert('Session expired. Please log in again.');
      return;
    }

    // Check for other errors
    const failedResponses = responses.filter(r => !r.ok);
    if (failedResponses.length > 0) {
      console.error('Some installer sheets failed to load');
    }

    // Get HTML content from successful responses
    const htmlContents = await Promise.all(
      responses.map(async (r, index) => {
        if (r.ok) {
          return await r.text();
        }
        return `<div style="padding: 40px; text-align: center;"><h2>Unit ${index + 1}</h2><p>Failed to load installer sheet</p></div>`;
      })
    );

    // Extract styles from first template (they're all the same)
    const styleMatch = htmlContents[0]?.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
    const templateStyles = styleMatch ? styleMatch[1] : '';

    // Build sheets content - extract body from each HTML and wrap in unit-sheet div
    const sheetsContent = htmlContents.map((html, index) => {
      const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
      const content = bodyMatch ? bodyMatch[1] : html;
      const divider = index < htmlContents.length - 1 ? '<div class="unit-divider"></div>' : '';
      return '<div class="unit-sheet">' + content + '</div>' + divider;
    }).join('\n');

    // Combine all sheets with Keder branded styling + original template styles
    const combinedHtml = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Installer Reference Sheets</title>
  <link href="https://fonts.googleapis.com/css2?family=Barlow:wght@400;500;600;700&family=Barlow+Condensed:wght@500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap" rel="stylesheet">
  <style>
    /* Original template styles */
    ${templateStyles}

    /* Wrapper/container styles */
    .print-page-wrapper {
      font-family: 'Barlow', -apple-system, BlinkMacSystemFont, sans-serif;
      background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
      min-height: 100vh;
    }
    .print-header {
      background: #000;
      padding: 25px 40px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      position: sticky;
      top: 0;
      z-index: 100;
      box-shadow: 0 4px 20px rgba(0,0,0,0.3);
    }
    .print-header-left { display: flex; align-items: center; gap: 20px; }
    .print-header-logo img { height: 50px; width: auto; display: block; }
    .print-header-title {
      color: #fff;
      font-family: 'Barlow Condensed', sans-serif;
      font-size: 18px;
      font-weight: 600;
      letter-spacing: 1px;
      text-transform: uppercase;
      white-space: nowrap;
    }
    .print-header-subtitle { color: #FFCD24; font-size: 14px; font-weight: 500; }
    .print-btn {
      background: #FFCD24;
      color: #000;
      border: none;
      padding: 14px 32px;
      font-family: 'Barlow', sans-serif;
      font-size: 16px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 1px;
      cursor: pointer;
      border-radius: 4px;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .print-btn:hover {
      background: #ffe066;
      transform: translateY(-2px);
      box-shadow: 0 4px 15px rgba(255, 205, 36, 0.4);
    }
    .print-btn svg { width: 20px; height: 20px; }
    .sheets-container { padding: 30px 40px 40px 40px; max-width: 1000px; margin: 0 auto; }
    .sheet-count-badge {
      background: rgba(255, 205, 36, 0.15);
      border: 1px solid #FFCD24;
      color: #FFCD24;
      padding: 8px 16px;
      border-radius: 20px;
      font-size: 13px;
      font-weight: 600;
      margin-bottom: 30px;
      display: inline-block;
    }
    .unit-sheet {
      background: #f0f2f5;
      border-radius: 8px;
      box-shadow: 0 8px 40px rgba(0,0,0,0.2);
      margin-bottom: 40px;
      padding: 20px;
    }
    .unit-sheet:last-child { margin-bottom: 0; }
    .unit-sheet .header { padding: 15px 20px !important; height: auto !important; overflow: visible !important; }
    .unit-divider {
      background: linear-gradient(90deg, transparent, #FFCD24, transparent);
      height: 3px;
      margin: 40px 0;
    }
    @media print {
      .print-page-wrapper { background: #fff; }
      .print-header { display: none !important; }
      .sheets-container { padding: 0; max-width: 100%; }
      .sheet-count-badge { display: none !important; }
      .unit-sheet {
        box-shadow: none;
        border-radius: 0;
        margin-bottom: 0;
        padding: 0;
        background: #fff;
        page-break-after: always;
      }
      .unit-sheet:last-child { page-break-after: auto; }
      .unit-sheet .header { padding: 10px 20px !important; height: 95px !important; overflow: hidden !important; }
      .unit-divider { display: none; }
    }
  </style>
</head>
<body class="print-page-wrapper">
  <div class="print-header">
    <div class="print-header-left">
      <div class="print-header-logo">
        <img src="https://kederscreens.com/wp-content/uploads/2025/07/Keder-logos-1-scaled.png" alt="Keder Screens">
      </div>
      <div>
        <div class="print-header-title">Installer Reference Sheets</div>
        <div class="print-header-subtitle">${unitCount} Screen${unitCount > 1 ? 's' : ''} &mdash; Ready to Print</div>
      </div>
    </div>
    <button class="print-btn" onclick="window.print()">
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"/>
      </svg>
      Print All Sheets
    </button>
  </div>
  <div class="sheets-container">
    <div class="sheet-count-badge">&#9679; ${unitCount} Installer Sheet${unitCount > 1 ? 's' : ''} Below</div>
    ${sheetsContent}
  </div>
</body>
</html>`;

    const blob = new Blob([combinedHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  } catch (error) {
    console.error('Error opening installer sheets:', error);
    alert('Failed to load installer sheets. Please try again.');
  }
};

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  pending_payment: 'bg-yellow-100 text-yellow-800',
  submitted: 'bg-yellow-100 text-yellow-800',
  pending_release: 'bg-orange-100 text-orange-800',
  approved: 'bg-blue-100 text-blue-800',
  in_production: 'bg-purple-100 text-purple-800',
  ready_to_ship: 'bg-indigo-100 text-indigo-800',
  shipped: 'bg-cyan-100 text-cyan-800',
  delivered: 'bg-teal-100 text-teal-800',
  install_scheduled: 'bg-blue-200 text-blue-900',
  installed: 'bg-green-200 text-green-900',
  warranty_registered: 'bg-emerald-100 text-emerald-800',
  completed: 'bg-green-100 text-green-800',
  closed: 'bg-green-100 text-green-800',
  needs_correction: 'bg-orange-100 text-orange-800',
  cancelled: 'bg-red-100 text-red-800',
  on_hold: 'bg-orange-100 text-orange-800',
};

const statusLabels: Record<string, string> = {
  draft: 'Draft',
  pending_payment: 'Pending Payment',
  submitted: 'Submitted',
  pending_release: 'Pending Release',
  approved: 'Approved',
  in_production: 'In Production',
  ready_to_ship: 'Ready to Ship',
  shipped: 'Shipped',
  delivered: 'Delivered',
  install_scheduled: 'Install Scheduled',
  installed: 'Installed',
  warranty_registered: 'Warranty Registered',
  completed: 'Completed',
  closed: 'Completed',
  needs_correction: 'Needs Correction',
  cancelled: 'Cancelled',
  on_hold: 'On Hold',
};

const freightStatusSteps: OrderStatus[] = [
  'submitted',
  'pending_release',
  'in_production',
  'ready_to_ship',
  'shipped',
  'delivered',
  'install_scheduled',
  'installed',
  'warranty_registered',
  'completed',
];

const willCallStatusSteps: OrderStatus[] = [
  'submitted',
  'pending_release',
  'in_production',
  'ready_to_ship',
  'shipped',
  'install_scheduled',
  'installed',
  'warranty_registered',
  'completed',
];

const partsOrderStatusSteps: OrderStatus[] = [
  'submitted',
  'pending_release',
  'ready_to_ship',
  'shipped',
  'delivered',
  'completed',
];

const willCallLabels: Record<string, string> = {
  ready_to_ship: 'Ready for Pickup',
  shipped: 'Picked Up',
};

// Screen Unit Visual Preview Component (Dark Theme)
function ScreenUnitPreview({ item }: { item: any }) {
  const maxWidth = item.maxWidth || item.max_width || Math.max(
    parseFloat(item.widthTop || item.width_top) || 0,
    parseFloat(item.widthMiddle || item.width_middle) || 0,
    parseFloat(item.widthBottom || item.width_bottom) || 0
  );
  const maxHeight = item.maxHeight || item.max_height || Math.max(
    parseFloat(item.heightLeft || item.height_left) || 0,
    parseFloat(item.heightCenter || item.height_center) || 0,
    parseFloat(item.heightRight || item.height_right) || 0
  );

  if (maxWidth === 0 || maxHeight === 0) return null;

  const maxDisplayWidth = 180;
  const maxDisplayHeight = 120;
  const scale = Math.min(maxDisplayWidth / maxWidth, maxDisplayHeight / maxHeight, 1.5);

  const displayWidth = maxWidth * scale;
  const displayHeight = maxHeight * scale;

  const housingHeight = 10;
  const weightBarHeight = 8;
  const trackMount = item.trackMount || item.track_mount || 'face';
  // Track width: face mount = narrower (3), inside/recessed/hybrid = wider (6)
  const trackWidth = trackMount === 'face' ? 3 : 6;

  const biasedCut = item.biasedCut || item.biased_cut;
  const biasAmount = item.biasedCutInches || item.biased_cut_inches || item.biasAmount || item.bias_amount || 0;
  const heightLeft = parseFloat(item.heightLeft || item.height_left) || 0;
  const heightRight = parseFloat(item.heightRight || item.height_right) || 0;
  // biased_cut_side stores which side is LONGER ('left' or 'right'). Use it as
  // the source of truth; heights are often equal so the comparison alone is
  // unreliable.
  const biasedCutSide = item.biasedCutSide || item.biased_cut_side;
  const leftIsHigher = biasedCutSide
    ? biasedCutSide === 'left'
    : heightLeft > heightRight;

  const uChannel = item.uChannel || item.u_channel || 'none';
  const hasLeftUChannel = uChannel === 'both_sides' || uChannel === 'left_side' || uChannel === 'two_sides';
  const hasRightUChannel = uChannel === 'both_sides' || uChannel === 'right_side' || uChannel === 'two_sides';
  const motorSide = item.motorSide || item.motor_side || 'right';

  // Window panel data (rendered as overlay rectangles inside the fabric area).
  // Designer terminology: Horizontal = ROWS (windows stacked top-to-bottom),
  // Vertical = COLUMNS (windows side-by-side). dividerType 'weld' = no gap between
  // windows; 'divider' = fabric strip of `dividerSize` inches between windows.
  const hasWindow = item.windowPanel || item.window_panel;
  const wd: any = item.windowData || item.window_data || {};
  const wOrient = (wd.orientation || 'horizontal') as 'horizontal' | 'vertical' | 'grid';
  const wCount = parseInt(wd.windowCount, 10) || 1;
  const wTopBorder = parseFloat(wd.borderTop) || 4;
  const wSideBorder = parseFloat(wd.borderSide) || 4;
  const wBottomBorder = parseFloat(wd.borderBottom) || 4;
  const wDividerType = wd.dividerType || 'weld';
  const wDividerSize = wDividerType === 'divider' ? (parseFloat(wd.dividerSize) || 0) : 0;

  return (
    <div className="bg-gray-800 border border-gray-600 rounded-lg p-3">
      <h5 className="text-xs font-semibold text-gray-400 mb-2">VISUAL PREVIEW</h5>
      <div className="flex justify-center">
        <div className="relative" style={{ width: displayWidth + 30, height: displayHeight + 40 }}>
          <div className="absolute top-0 text-center text-[9px] text-gray-400" style={{ left: 15, right: 15 }}>
            {maxWidth}"
          </div>
          <div className="absolute right-0 flex items-center text-[9px] text-gray-400" style={{ top: 15, height: displayHeight }}>
            <span className="transform rotate-90">{maxHeight}"</span>
          </div>

          <div
            className="absolute border border-gray-500 bg-gray-900"
            style={{ left: 15, top: 15, width: displayWidth, height: displayHeight }}
          >
            <div
              className="absolute top-0 left-0 right-0 bg-gray-600 flex items-center justify-center"
              style={{ height: housingHeight }}
            >
              <span className="text-[6px] text-gray-300 font-medium">HOUSING</span>
            </div>

            <div
              className="absolute left-0 bg-gray-700"
              style={{
                top: housingHeight,
                width: trackWidth,
                height: displayHeight - housingHeight - weightBarHeight,
                borderLeft: hasLeftUChannel ? '2px solid #3b82f6' : 'none'
              }}
            />

            <div
              className="absolute right-0 bg-gray-700"
              style={{
                top: housingHeight,
                width: trackWidth,
                height: displayHeight - housingHeight - weightBarHeight,
                borderRight: hasRightUChannel ? '2px solid #3b82f6' : 'none'
              }}
            />

            <div
              className="absolute bg-green-900/60 border border-green-600"
              style={{
                left: trackWidth,
                right: trackWidth,
                top: housingHeight,
                bottom: weightBarHeight,
              }}
            >
              {hasWindow ? (() => {
                // Layout windows inside the fabric area, accounting for borders.
                const fabricInnerW = displayWidth - trackWidth * 2;
                const fabricInnerH = displayHeight - housingHeight - weightBarHeight;
                // Scale border inches to display pixels using the same scale as the screen.
                const sb = wSideBorder * scale;
                const tb = wTopBorder * scale;
                const bb = wBottomBorder * scale;
                const dv = wDividerSize * scale;
                const winAreaW = Math.max(0, fabricInnerW - sb * 2);
                const winAreaH = Math.max(0, fabricInnerH - tb - bb);

                // Designer behavior (NewOrderPage WindowDesigner ~line 791): both
                // horizontal and vertical produce side-by-side windows in a single row
                // — the "orientation" label only changes the per-pane max dimensions
                // (horizontal pane = 106"W × 52"H, vertical pane = 52"W × 106"H).
                // Only `grid` is 2D, using horizontal/verticalDividers.
                let cols = 1, rows = 1;
                if (wOrient === 'horizontal' || wOrient === 'vertical') {
                  cols = Math.max(1, wCount);
                  rows = 1;
                } else if (wOrient === 'grid') {
                  const hd = (parseInt(wd.horizontalDividers, 10) || 0);
                  const vd = (parseInt(wd.verticalDividers, 10) || 0);
                  rows = Math.max(1, hd + 1);
                  cols = Math.max(1, vd + 1);
                }

                const winW = (winAreaW - dv * (cols - 1)) / cols;
                const winH = (winAreaH - dv * (rows - 1)) / rows;

                const cells = [];
                let idx = 0;
                for (let r = 0; r < rows; r++) {
                  for (let c = 0; c < cols; c++) {
                    if (idx >= wCount) break;
                    const x = sb + c * (winW + dv);
                    const y = tb + r * (winH + dv);
                    cells.push(
                      <div
                        key={`win-${r}-${c}`}
                        className="absolute bg-blue-200/40 border border-blue-300"
                        style={{ left: x, top: y, width: winW, height: winH }}
                      />
                    );
                    idx++;
                  }
                }
                return (
                  <>
                    {cells}
                    <div className="absolute top-0.5 left-1 text-[6px] text-blue-200 font-medium">
                      {wCount}W {wDividerType === 'weld' ? 'WLD' : `${wDividerSize}"D`}
                    </div>
                  </>
                );
              })() : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-[8px] text-green-400 font-medium">FABRIC</span>
                </div>
              )}
            </div>

            {biasedCut && biasAmount > 0 ? (
              <svg
                className="absolute bottom-0 left-0 right-0"
                style={{ height: weightBarHeight + 8 }}
                viewBox={`0 0 ${displayWidth} ${weightBarHeight + 8}`}
              >
                {(() => {
                  const biasVisual = Math.min(parseFloat(String(biasAmount)) * 2, 8);
                  // Bar droops lower on the taller side (higher Y = lower on screen)
                  const leftY = leftIsHigher ? biasVisual : 0;
                  const rightY = leftIsHigher ? 0 : biasVisual;
                  return (
                    <>
                      <polygon
                        points={`0,${leftY} ${displayWidth},${rightY} ${displayWidth},${rightY + weightBarHeight} 0,${leftY + weightBarHeight}`}
                        fill="#6b7280"
                        stroke="#4b5563"
                        strokeWidth="0.5"
                      />
                      <text x={displayWidth / 2} y={(leftY + rightY) / 2 + weightBarHeight / 2 + 2} textAnchor="middle" fill="#d1d5db" fontSize="6">
                        BAR
                      </text>
                      <text
                        x={leftIsHigher ? displayWidth - 2 : 2}
                        y={leftIsHigher ? rightY + 10 : leftY + 10}
                        textAnchor={leftIsHigher ? "end" : "start"}
                        fill="#f59e0b"
                        fontSize="7"
                        fontWeight="bold"
                      >
                        {biasAmount}"
                      </text>
                    </>
                  );
                })()}
              </svg>
            ) : (
              <div
                className="absolute bottom-0 left-0 right-0 bg-gray-500 flex items-center justify-center"
                style={{ height: weightBarHeight }}
              >
                <span className="text-[6px] text-gray-200">BAR</span>
              </div>
            )}

            <div
              className={`absolute bg-yellow-500 text-[6px] text-black font-bold px-1 rounded ${
                motorSide === 'left' ? 'left-1' : 'right-1'
              }`}
              style={{ top: housingHeight + 2 }}
            >
              M
            </div>
          </div>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-2 justify-center text-[8px] text-gray-400">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 bg-gray-600 rounded"></span> Housing/Bar
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 bg-green-900 border border-green-600 rounded"></span> Fabric
        </span>
        {(hasLeftUChannel || hasRightUChannel) && (
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 border-2 border-blue-500 rounded"></span> U-Channel
          </span>
        )}
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 bg-yellow-500 rounded"></span> Motor ({motorSide})
        </span>
        {biasedCut && biasAmount > 0 && (
          <span className="text-orange-500 font-medium">Bias: {biasAmount}"</span>
        )}
      </div>
    </div>
  );
}

// Window Panel Details Component.
// The window-design data persists as a JSONB blob in `order_items.window_data` and
// surfaces on the API as `item.windowData`. Earlier this component read flat
// properties like `item.windowOrientation` / `item.windowCount` that never existed,
// so the display always fell back to the defaults (Horizontal, 1 window, no borders).
function WindowPanelDetails({ item }: { item: any }) {
  const hasWindow = item.windowPanel || item.window_panel || item.hasWindow;
  if (!hasWindow) return null;

  const wd = item.windowData || item.window_data || {};

  const orientation = wd.orientation || item.windowOrientation || item.window_orientation || 'Horizontal';
  const windowCount = wd.windowCount || item.windowCount || item.window_count || 1;
  const windowWidth = wd.windowWidth || item.windowWidth || item.window_width;
  const windowHeight = wd.windowHeight || item.windowHeight || item.window_height;
  const topBorder = wd.borderTop ?? wd.topBorderInches ?? item.topBorderInches ?? item.top_border_inches;
  const sideBorder = wd.borderSide ?? wd.sideBorderInches ?? item.sideBorderInches ?? item.side_border_inches;
  const bottomBorder = wd.borderBottom ?? wd.bottomBorderInches ?? item.bottomBorderInches ?? item.bottom_border_inches;
  const dividerType = wd.dividerType;
  const dividerSize = wd.dividerSize;

  return (
    <div className="bg-blue-50 dark:bg-blue-950 rounded-lg p-3 border border-blue-200 dark:border-blue-700">
      <h6 className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-2">WINDOW PANEL</h6>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div><span className="text-gray-500">Orientation:</span> <span className="text-gray-900 dark:text-white capitalize">{orientation}</span></div>
        <div><span className="text-gray-500"># of Windows:</span> <span className="text-gray-900 dark:text-white">{windowCount}</span></div>
        {windowWidth && <div><span className="text-gray-500">Window Width:</span> <span className="text-gray-900 dark:text-white">{windowWidth}"</span></div>}
        {windowHeight && <div><span className="text-gray-500">Window Height:</span> <span className="text-gray-900 dark:text-white">{windowHeight}"</span></div>}
        {topBorder != null && topBorder !== '' && <div><span className="text-gray-500">Top Border:</span> <span className="text-gray-900 dark:text-white">{topBorder}"</span></div>}
        {sideBorder != null && sideBorder !== '' && <div><span className="text-gray-500">Side Border:</span> <span className="text-gray-900 dark:text-white">{sideBorder}"</span></div>}
        {bottomBorder != null && bottomBorder !== '' && <div><span className="text-gray-500">Bottom Border:</span> <span className="text-gray-900 dark:text-white">{bottomBorder}"</span></div>}
        {dividerType && <div><span className="text-gray-500">Divider:</span> <span className="text-gray-900 dark:text-white capitalize">{dividerType}{dividerType === 'divider' && dividerSize ? ` (${dividerSize}")` : ''}</span></div>}
      </div>
    </div>
  );
}

export const OrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const fromNewOrder = (location.state as any)?.fromNewOrder === true;
  const [order, setOrder] = useState<Order | null>(null);
  const [items, setItems] = useState<OrderItem[]>([]);
  const [orderData, setOrderData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [invoiceChanges, setInvoiceChanges] = useState<Array<{ date: string; reason: string; delta: number; fromTotal: number; toTotal: number }>>([]);
  const [achDiscountPercent, setAchDiscountPercent] = useState<number>(0);
  const [availableCredit, setAvailableCredit] = useState<number>(0);
  const [authorizeAchDiscountPercent, setAuthorizeAchDiscountPercent] = useState<number>(0);
  const [helcimAchDiscountPercent, setHelcimAchDiscountPercent] = useState<number>(0);
  // Dealer's internal commission tracking toggle — hides "Record Your Sale"
  // card + "Your Margin" widget when off. Defaults ON until settings load.
  const [trackingEnabled, setTrackingEnabled] = useState<boolean>(true);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [shortageInfo, setShortageInfo] = useState<{
    onHold: boolean;
    holdSince: string | null;
    items: Array<{ description: string; shortageQuantity: number; estimatedLeadTimeDays: number }>;
  } | null>(null);
  const [productionProgress, setProductionProgress] = useState<{
    totalSteps: number;
    completedSteps: number;
    inProgressSteps: number;
    progressPercent: number;
  } | null>(null);

  const [linkedDesigns, setLinkedDesigns] = useState<any[]>([]);
  const [expandedDesign, setExpandedDesign] = useState<string | null>(null);

  const toggleItemExpand = (itemId: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const [showScheduleInstallModal, setShowScheduleInstallModal] = useState(false);
  const [showMarkInstalledModal, setShowMarkInstalledModal] = useState(false);
  const [installForm, setInstallForm] = useState({
    installDate: '',
    installTime: '',
    notes: '',
    installerName: '',
  });

  const [sellPriceForm, setSellPriceForm] = useState({
    customerSellPrice: '',
    dateSoldToCustomer: '',
  });
  const [savingSellPrice, setSavingSellPrice] = useState(false);
  const [sellPriceSaved, setSellPriceSaved] = useState(false);
  const [invoiceBalance, setInvoiceBalance] = useState<number | null>(null);

  // Authorize.net payment modal state
  const [showCardModal, setShowCardModal] = useState(false);
  const [cardModalAmount, setCardModalAmount] = useState(0);
  // Card fee consent → hosted Stripe checkout.
  const [cardConsentOpen, setCardConsentOpen] = useState(false);
  const [consentAmount, setConsentAmount] = useState(0);
  const [consentCredit, setConsentCredit] = useState(0);
  const [cardModalTab, setCardModalTab] = useState<'saved' | 'card' | 'bank'>('card');
  const [cardForm, setCardForm] = useState({ cardNumber: '', expMonth: '', expYear: '', cvv: '' });
  const [saveForFuture, setSaveForFuture] = useState(true);
  const [helcimCheckoutToken, setHelcimCheckoutToken] = useState<string | null>(null);
  const [helcimSecretToken, setHelcimSecretToken] = useState<string | null>(null);
  const [cardProcessing, setCardProcessing] = useState(false);
  const [savedMethods, setSavedMethods] = useState<any[]>([]);
  const [selectedSavedMethod, setSelectedSavedMethod] = useState<string | null>(null);
  const [loadingSavedMethods, setLoadingSavedMethods] = useState(false);

  // ACH address collection (when dealer has no address on file)
  const [achAddressForm, setAchAddressForm] = useState({ street: '', city: '', state: '', zip: '' });
  const hasAddress = !!(user as any)?.addressStreet || !!(user as any)?.address_street || !!(user as any)?.addressZip || !!(user as any)?.address_zip;

  useEffect(() => {
    if (id) {
      fetchOrder();
      // Fetch invoice to get real balance (after dealer discount)
      ordersApi.getInvoice(id)
        .then(inv => {
          if (inv && typeof inv.balanceDue === 'number') {
            setInvoiceBalance(inv.balanceDue);
          }
        })
        .catch(() => { /* invoice may not exist yet for drafts */ });
      // Why-the-invoice-changed history (final shipping adjusted, order corrected, …)
      ordersApi.getInvoiceChanges(id)
        .then(ch => setInvoiceChanges(Array.isArray(ch) ? ch : []))
        .catch(() => { /* none / not available */ });
    }
    paymentApi.getAchSettings()
      .then(settings => {
        setAchDiscountPercent(settings.achDiscountPercent);
        setAuthorizeAchDiscountPercent((settings as any).authorizeAchDiscountPercent || 0);
        setHelcimAchDiscountPercent((settings as any).helcimAchDiscountPercent || 0);
      })
      .catch(err => console.error('Failed to fetch ACH settings:', err));
    // Load available store credit (silent fail — page works without it)
    import('../services/api').then(({ dealerCreditApi }) =>
      dealerCreditApi.getMyAvailable()
        .then(r => setAvailableCredit(Number(r?.available || 0)))
        .catch(() => {}),
    );
    // Load dealer commission-tracking toggle (defaults ON on error).
    import('../services/api').then(({ dealerCommissionSettingsApi }) =>
      dealerCommissionSettingsApi.get()
        .then(s => setTrackingEnabled(s?.commission_tracking_enabled ?? true))
        .catch(() => {}),
    );
  }, [id]);

  // Auto-scroll to invoice when arriving from new order confirmation
  useEffect(() => {
    if (fromNewOrder && !loading) {
      const timer = setTimeout(() => {
        document.getElementById('invoice-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [fromNewOrder, loading]);

  // Clear navigation state so refresh doesn't re-show the banner
  useEffect(() => {
    if (fromNewOrder) {
      window.history.replaceState({}, document.title);
    }
  }, [fromNewOrder]);

  // Pre-fill the sell-price form with the order total (dealer cost) when the
  // dealer hasn't recorded one yet — a default that lets them log "no markup"
  // commission rows in one click instead of re-typing the order total.
  useEffect(() => {
    if (!order) return;
    const existing = (order as any).customerSellPrice ?? (order as any).customer_sell_price;
    const hasRecorded = existing !== null && existing !== undefined && Number(existing) > 0;
    if (hasRecorded) return;
    if (sellPriceForm.customerSellPrice) return;
    const total = order.totalAmount ?? (order as any).total_amount;
    if (total && Number(total) > 0) {
      setSellPriceForm(prev => ({ ...prev, customerSellPrice: String(total) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id, (order as any)?.totalAmount, (order as any)?.customerSellPrice]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const [orderResponse, itemsData] = await Promise.all([
        ordersApi.get(id!),
        ordersApi.getItems(id!).catch(() => []),
      ]);
      setOrder(orderResponse);
      setItems(itemsData || []);

      if ((orderResponse as any).order_data || (orderResponse as any).orderData) {
        try {
          const rawData = (orderResponse as any).order_data || (orderResponse as any).orderData;
          const parsed = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
          setOrderData(parsed);
        } catch (e) {
          console.warn('Could not parse order_data:', e);
        }
      }

      // Fetch production progress (non-blocking)
      const productionStatuses = ['pending_release', 'approved', 'confirmed', 'in_production', 'ready_to_ship', 'shipped', 'delivered', 'installed', 'completed'];
      if (productionStatuses.includes(orderResponse.status)) {
        ordersApi.getProductionProgress(id!)
          .then(progress => setProductionProgress(progress))
          .catch(() => { /* supplementary */ });
      }

      // Fetch shortage info (non-blocking - don't fail the page if this errors)
      if ((orderResponse as any).shortage_hold || (orderResponse as any).shortageHold) {
        ordersApi.getShortageInfo(id!)
          .then(info => {
            if (info && info.onHold) {
              setShortageInfo(info);
            }
          })
          .catch(() => {
            // Silently ignore - shortage info is supplementary
          });
      }

      // Fetch linked screen designs (non-blocking)
      const apiUrl = import.meta.env.VITE_API_URL || '/api';
      const token = localStorage.getItem('auth_token');
      fetch(`${apiUrl}/screen-designs/by-order/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(res => res.ok ? res.json() : [])
        .then(designs => setLinkedDesigns(designs))
        .catch(() => { /* supplementary */ });
    } catch (err: any) {
      setError(err.message || 'Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  const openAuthorizeModal = (tab: 'saved' | 'card' | 'bank', customAmount?: number) => {
    if (!order) return;
    const totalAmount = customAmount != null ? customAmount : (invoiceBalance !== null ? invoiceBalance : (order.totalAmount || (order as any).total_amount || 0));
    setCardModalAmount(totalAmount);
    setCardForm({ cardNumber: '', expMonth: '', expYear: '', cvv: '' });
    setSaveForFuture(true);
    setSelectedSavedMethod(null);
    setCardModalTab(tab);
    setShowCardModal(true);
    // Load saved methods from both Authorize.net (cards) and Helcim (bank accounts)
    setLoadingSavedMethods(true);
    Promise.all([
      authorizeApi.getSavedMethods().catch(() => []),
      helcimApi.getSavedBankAccounts().catch(() => []),
    ])
      .then(([authMethods, helcimBanks]) => {
        // Filter out Authorize.net bank accounts (legacy) — only keep cards
        const cards = authMethods.filter(m => m.paymentType === 'card');
        const allMethods = [...cards, ...helcimBanks.map(b => ({ ...b, provider: 'helcim' as const }))];
        setSavedMethods(allMethods);
        if (allMethods.length > 0) setSelectedSavedMethod(allMethods[0].id);
      })
      .catch(() => setSavedMethods([]))
      .finally(() => setLoadingSavedMethods(false));
  };

  // Redirect to the hosted Stripe Checkout page (has Link + Apple/Google Pay and
  // handles the card fee + debit refund). Card goes through the consent popup
  // first (see handleSubmitOrder); ACH comes straight here (no fee).
  const proceedHostedCheckout = async (
    paymentMethodType: 'card' | 'us_bank_account',
    totalAmount: number,
    creditApplied?: number,
  ) => {
    if (!order) return;
    try {
      setSubmitting(true);
      setError(null);
      const rawData = orderData || (order as any).order_data || (order as any).orderData;
      const parsedOrderData = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
      const orderInfo = parsedOrderData?.order || parsedOrderData || {};
      const invoiceInfo = parsedOrderData?.invoice || {};
      const checkoutRequest = {
        invoice: invoiceInfo,
        order: orderInfo,
        dealer: {
          id: user?.id || (order as any).dealer_contact_id,
          dealerId: (order as any).dealer_id || user?.dealerId,
          email: user?.email || (order as any).contact_email,
          company: user?.company || (order as any).company_name,
          contactName: user?.name || (order as any).contact_name,
        },
        paymentAmount: Math.round(totalAmount * 100),
        orderType: (order as any).order_type || 'screen',
        paymentMethodType,
        existingOrderId: order.id,
        creditApplied: creditApplied && creditApplied > 0 ? creditApplied : undefined,
      };
      const result = await paymentApi.createCheckout(checkoutRequest);
      if (result.success && result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      } else {
        setError(result.error || 'Failed to create payment session');
      }
    } catch (err: any) {
      console.error('[OrderDetailPage] Payment error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to process payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitOrder = async (paymentMethodType: 'card' | 'us_bank_account' | 'authorize_net' | 'authorize_bank' = 'card', customAmount?: number, creditApplied?: number) => {
    if (!order) return;
    // Legacy Authorize.net/Helcim buttons → Stripe
    if (paymentMethodType === 'authorize_net') return handleSubmitOrder('card', customAmount, creditApplied);
    if (paymentMethodType === 'authorize_bank') return handleSubmitOrder('us_bank_account', customAmount, creditApplied);

    const totalAmount = customAmount != null ? customAmount : (invoiceBalance !== null ? invoiceBalance : (order.totalAmount || (order as any).total_amount || 0));
    if (Math.round(totalAmount * 100) < 100) {
      setError('Order total must be at least $1.00 to process payment');
      return;
    }

    // Card → show the fee-consent popup first, then redirect to hosted checkout.
    if (paymentMethodType === 'card') {
      setConsentAmount(totalAmount);
      setConsentCredit(creditApplied || 0);
      setCardConsentOpen(true);
      return;
    }
    // ACH → hosted checkout directly (no fee).
    await proceedHostedCheckout('us_bank_account', totalAmount, creditApplied);
  };

  /** Wait for Accept.js to be ready (loaded from index.html <head>) */
  const loadAcceptJs = async () => {
    if ((window as any).Accept) return;
    await new Promise<void>((resolve, reject) => {
      let attempts = 0;
      const checkReady = () => {
        if ((window as any).Accept) { resolve(); return; }
        attempts++;
        if (attempts > 100) { reject(new Error('Accept.js failed to load')); return; }
        setTimeout(checkReady, 100);
      };
      checkReady();
    });
  };

  /** Tokenize card or bank data via Accept.js dispatchData */
  const tokenizeWithAcceptJs = (secureData: any): Promise<{ dataDescriptor: string; dataValue: string }> => {
    return new Promise((resolve, reject) => {
      (window as any).Accept.dispatchData(secureData, (response: any) => {
        if (response.messages.resultCode === 'Error') {
          const msgs = response.messages.message;
          const code = msgs[0]?.code || '';
          const msg = msgs.map((m: any) => m.text).join(', ');
          console.error('[Accept.js] Error code:', code, 'Text:', msg, 'Full response:', JSON.stringify(response.messages));
          reject(new Error(`${msg} (${code})`));
        } else {
          resolve({ dataDescriptor: response.opaqueData.dataDescriptor, dataValue: response.opaqueData.dataValue });
        }
      });
    });
  };

  /** Pay with new card via Authorize.net */
  const handleAuthorizeNetPayment = async () => {
    if (!order) return;
    setCardProcessing(true);
    setError(null);

    try {
      await loadAcceptJs();

      const apiLoginId = import.meta.env.VITE_AUTHORIZE_API_LOGIN_ID;
      const clientKey = import.meta.env.VITE_AUTHORIZE_CLIENT_KEY;
      if (!apiLoginId || !clientKey) {
        setError('Direct card payment is not configured.');
        setCardProcessing(false);
        return;
      }

      const opaqueData = await tokenizeWithAcceptJs({
        authData: { apiLoginID: apiLoginId, clientKey },
        cardData: {
          cardNumber: cardForm.cardNumber.replace(/\s/g, ''),
          month: cardForm.expMonth.padStart(2, '0'),
          year: cardForm.expYear.length === 2 ? `20${cardForm.expYear}` : cardForm.expYear,
          cardCode: cardForm.cvv,
        },
      });

      const result = await authorizeApi.charge({
        opaqueDataDescriptor: opaqueData.dataDescriptor,
        opaqueDataValue: opaqueData.dataValue,
        amount: Math.round(cardModalAmount * 100),
        orderId: order.id,
        orderNumber: (order as any).orderNumber || (order as any).order_number,
        dealer: {
          email: user?.email || '',
          company: user?.company || '',
          contactName: user?.name || '',
        },
        saveForFuture,
      });

      if (result.success) {
        setShowCardModal(false);
        await fetchOrder();
        alert(`Payment of $${cardModalAmount.toFixed(2)} processed successfully! Transaction ID: ${result.transactionId}`);
      } else {
        setError(result.error || 'Payment was declined. Please check your card details and try again.');
      }
    } catch (err: any) {
      console.error('[OrderDetailPage] Authorize.net card error:', err);
      setError(err.message || 'Payment processing failed. Please try again.');
    } finally {
      setCardProcessing(false);
    }
  };

  /** Pay with saved card/bank */
  const handleChargeSavedMethod = async () => {
    if (!order || !selectedSavedMethod) return;
    setCardProcessing(true);
    setError(null);

    try {
      // Check if selected method is a Helcim bank account
      const selectedMethod = savedMethods.find(m => m.id === selectedSavedMethod);
      const isHelcim = (selectedMethod as any)?.provider === 'helcim';

      let result: { success: boolean; transactionId?: string; error?: string };

      if (isHelcim) {
        result = await helcimApi.chargeSavedBank({
          profileId: selectedSavedMethod,
          amount: Math.round(cardModalAmount * 100),
          orderId: order.id,
          orderNumber: (order as any).orderNumber || (order as any).order_number,
        });
      } else {
        result = await authorizeApi.chargeSaved({
          paymentProfileId: selectedSavedMethod,
          amount: Math.round(cardModalAmount * 100),
          orderId: order.id,
          orderNumber: (order as any).orderNumber || (order as any).order_number,
        });
      }

      if (result.success) {
        setShowCardModal(false);
        await fetchOrder();
        alert(`Payment of $${cardModalAmount.toFixed(2)} processed successfully! Transaction ID: ${result.transactionId}`);
      } else {
        setError(result.error || 'Payment was declined.');
      }
    } catch (err: any) {
      console.error('[OrderDetailPage] Saved method charge error:', err);
      setError(err.message || 'Payment processing failed. Please try again.');
    } finally {
      setCardProcessing(false);
    }
  };

  /** Pay with bank account (ACH) via HelcimPay.js hosted modal */
  const handleBankAccountPayment = async () => {
    if (!order) return;

    // If no address on file, validate and save the inline address form first
    if (!hasAddress) {
      if (!achAddressForm.city.trim() || !achAddressForm.state.trim() || !achAddressForm.zip.trim()) {
        setError('Please enter your city, state, and ZIP code to proceed with ACH payment.');
        return;
      }
      if (!/^\d{5}(-\d{4})?$/.test(achAddressForm.zip.trim())) {
        setError('Please enter a valid ZIP code.');
        return;
      }
    }

    setCardProcessing(true);
    setError(null);

    try {
      // Save address if entered from inline form
      if (!hasAddress && achAddressForm.zip.trim()) {
        await authApi.updateAddress({
          street: achAddressForm.street.trim(),
          city: achAddressForm.city.trim(),
          state: achAddressForm.state.trim(),
          zip: achAddressForm.zip.trim(),
        });
      }

      // 1. Initialize HelcimPay.js checkout session
      const session = await helcimApi.initializeCheckout({
        amount: Math.round(cardModalAmount * 100),
        orderId: order.id,
        orderNumber: (order as any).orderNumber || (order as any).order_number,
        dealer: {
          email: user?.email || '',
          company: (user as any)?.companyName || user?.company || (order as any).company_name || '',
          contactName: user?.name || '',
        },
      });

      setHelcimCheckoutToken(session.checkoutToken);
      setHelcimSecretToken(session.secretToken);

      // 2. Load HelcimPay.js script if not already loaded
      if (!(window as any).appendHelcimPayIframe) {
        await new Promise<void>((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://secure.helcim.app/helcim-pay/services/start.js';
          script.onload = () => resolve();
          script.onerror = () => reject(new Error('Failed to load Helcim payment form'));
          document.head.appendChild(script);
        });
      }

      // 3. Listen for HelcimPay.js events
      const eventHandler = async (event: MessageEvent) => {
        const eventData = event.data;
        if (!eventData || typeof eventData !== 'object') return;

        const eventName = eventData.eventName || '';
        if (!eventName.includes('helcim-pay-js')) return;

        const eventStatus = eventData.eventStatus;

        if (eventStatus === 'SUCCESS') {
          window.removeEventListener('message', eventHandler);
          try {
            // Parse transaction data
            let txData = eventData.eventMessage;
            if (typeof txData === 'string') {
              try { txData = JSON.parse(txData); } catch { /* keep as string */ }
            }

            // 4. Validate and record payment on backend
            const result = await helcimApi.validatePayment({
              checkoutToken: session.checkoutToken,
              secretToken: session.secretToken,
              transactionData: txData,
              orderId: order.id,
              amountCents: session.chargedAmountCents,
              achDiscountPercent: session.achDiscountPercent,
              achDiscountAmountCents: session.achDiscountAmountCents,
            });

            if (result.success) {
              setShowCardModal(false);
              setHelcimCheckoutToken(null);
              setHelcimSecretToken(null);
              await fetchOrder();
              const discountNote = session.achDiscountPercent > 0
                ? ` (${session.achDiscountPercent}% ACH discount saved $${(session.achDiscountAmountCents / 100).toFixed(2)})`
                : '';
              alert(`ACH payment of $${(session.chargedAmountCents / 100).toFixed(2)} processed successfully!${discountNote} Transaction ID: ${result.transactionId}`);
            } else {
              setError(result.error || 'Payment validation failed.');
            }
          } catch (valErr: any) {
            console.error('[OrderDetailPage] ACH validation error:', valErr);
            setError(valErr.message || 'Payment validation failed.');
          } finally {
            setCardProcessing(false);
          }
        } else if (eventStatus === 'ABORTED') {
          window.removeEventListener('message', eventHandler);
          setCardProcessing(false);
          setHelcimCheckoutToken(null);
          setHelcimSecretToken(null);
        }
      };

      window.addEventListener('message', eventHandler);

      // 4. Open the HelcimPay.js modal
      (window as any).appendHelcimPayIframe(session.checkoutToken);

    } catch (err: any) {
      console.error('[OrderDetailPage] ACH payment error:', err);
      setError(err.response?.data?.message || err.message || 'ACH payment initialization failed. Please try again.');
      setCardProcessing(false);
    }
  };

  /** Delete a saved payment method */
  const handleDeleteSavedMethod = async (id: string) => {
    if (!confirm('Remove this saved payment method?')) return;
    try {
      const method = savedMethods.find(m => m.id === id);
      const isHelcim = (method as any)?.provider === 'helcim';
      if (isHelcim) {
        await helcimApi.deleteSavedBankAccount(id);
      } else {
        await authorizeApi.deleteSavedMethod(id);
      }
      setSavedMethods(prev => prev.filter(m => m.id !== id));
      if (selectedSavedMethod === id) setSelectedSavedMethod(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete payment method');
    }
  };

  const handleCancelOrder = async () => {
    if (!order || !confirm('Are you sure you want to cancel this order?')) return;
    try {
      setSubmitting(true);
      await ordersApi.cancel(order.id);
      await fetchOrder();
    } catch (err: any) {
      setError(err.message || 'Failed to cancel order');
    } finally {
      setSubmitting(false);
    }
  };

  const handleScheduleInstall = async () => {
    if (!order || !installForm.installDate) return;
    try {
      setSubmitting(true);
      setError(null);
      await ordersApi.scheduleInstall(order.id, {
        installDate: installForm.installDate,
        installTime: installForm.installTime || undefined,
        installerName: installForm.installerName || undefined,
        notes: installForm.notes || undefined,
      });
      setShowScheduleInstallModal(false);
      setInstallForm({ installDate: '', installTime: '', notes: '', installerName: '' });
      await fetchOrder();
    } catch (err: any) {
      setError(err.message || 'Failed to schedule installation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRescheduleInstall = async () => {
    if (!order || !installForm.installDate) return;
    try {
      setSubmitting(true);
      setError(null);
      await ordersApi.rescheduleInstall(order.id, {
        installDate: installForm.installDate,
        installTime: installForm.installTime || undefined,
        installerName: installForm.installerName || undefined,
        notes: installForm.notes || undefined,
      });
      setShowScheduleInstallModal(false);
      setInstallForm({ installDate: '', installTime: '', notes: '', installerName: '' });
      await fetchOrder();
    } catch (err: any) {
      setError(err.message || 'Failed to reschedule installation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkInstalled = async () => {
    if (!order) return;
    try {
      setSubmitting(true);
      setError(null);
      await ordersApi.markInstalled(order.id, {
        installerName: installForm.installerName || undefined,
      });
      setShowMarkInstalledModal(false);
      setInstallForm({ installDate: '', installTime: '', notes: '', installerName: '' });
      await fetchOrder();
    } catch (err: any) {
      setError(err.message || 'Failed to mark as installed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveSellPrice = async () => {
    if (!order) return;
    // Empty is allowed — backend defaults to the order total (dealer cost).
    const parsed = parseFloat(sellPriceForm.customerSellPrice);
    const sellPrice = Number.isFinite(parsed) && parsed > 0
      ? parsed
      : (order.totalAmount ?? (order as any).total_amount ?? 0);
    try {
      setSavingSellPrice(true);
      setError(null);
      await ordersApi.update(order.id, {
        customerSellPrice: sellPrice,
        dateSoldToCustomer: sellPriceForm.dateSoldToCustomer || undefined,
      });
      setSellPriceSaved(true);
      await fetchOrder();
      setTimeout(() => setSellPriceSaved(false), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save sell price');
    } finally {
      setSavingSellPrice(false);
    }
  };

  const canRecordSellPrice = () => {
    if (!order) return false;
    // Master toggle — dealers who turned off internal commission tracking
    // shouldn't see the "Record Your Sale" card at all, even on eligible orders.
    if (!trackingEnabled) return false;
    const eligibleStatuses = ['shipped', 'delivered', 'install_scheduled', 'installed', 'warranty_registered', 'completed', 'closed'];
    return eligibleStatuses.includes(order.status);
  };

  const hasSellPriceRecorded = () => {
    if (!order) return false;
    const sellPrice = (order as any).customerSellPrice || (order as any).customer_sell_price;
    return sellPrice !== null && sellPrice !== undefined && sellPrice > 0;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateShort = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const getCurrentStepIndex = () => {
    if (!order) return -1;
    const steps = isPartsOrder() ? partsOrderStatusSteps : isWillCall ? willCallStatusSteps : freightStatusSteps;
    return steps.indexOf(order.status);
  };

  const isPartsOrder = () => {
    return (order as any)?.orderType === 'parts' || (order as any)?.order_type === 'parts';
  };

  // Calculate dealer discount factor — dealer sees their net price, not MSRP.
  // Prefer the authoritative order.discount_rate (e.g. 50.00 = 50% off). Reverse-
  // math fallback only used when discount_rate is missing on legacy orders, and
  // even then it strips packaging + accessories + tax (not just shipping) so
  // they don't get smeared into the per-screen display.
  const getDealerDiscountFactor = () => {
    const rate = (order as any)?.discountRate ?? (order as any)?.discount_rate;
    if (rate !== undefined && rate !== null && !Number.isNaN(parseFloat(rate))) {
      const r = parseFloat(rate);
      if (r >= 0 && r <= 100) return 1 - r / 100;
    }
    // Fallback for legacy orders without discount_rate
    const localInvoiceInfo = orderData?.invoice || {};
    const msrpSubtotal = order?.subtotal || localInvoiceInfo?.subtotal || 0;
    const dealerTotal = order?.totalAmount || (order as any)?.total_amount || localInvoiceInfo?.total || 0;
    const shippingAmount = (order as any)?.shippingAmount || (order as any)?.shipping_cost || localInvoiceInfo?.freight || 0;
    const packagingAmount = (order as any)?.packagingAmount || (order as any)?.packaging_amount || 0;
    const taxAmount = (order as any)?.taxAmount || (order as any)?.tax_amount || localInvoiceInfo?.tax || 0;
    const accessoryAmount = (order as any)?.accessoriesAmount || 0;
    const dealerProductTotal = dealerTotal - shippingAmount - packagingAmount - taxAmount - accessoryAmount;
    if (msrpSubtotal > 0 && dealerProductTotal > 0) {
      return dealerProductTotal / msrpSubtotal;
    }
    return 1;
  };

  const dealerDiscountFactor = getDealerDiscountFactor();

  // Apply dealer discount to a price (convert MSRP to dealer price)
  const toDealerPrice = (msrpPrice: number) => {
    return msrpPrice * dealerDiscountFactor;
  };

  const getDisplayItems = () => {
    if (items.length > 0) {
      // Apply dealer discount to existing items
      return items.map(item => ({
        ...item,
        unitPrice: toDealerPrice(item.unitPrice || 0),
        uChannelPrice: toDealerPrice((item as any).uChannelPrice || 0),
        totalPrice: toDealerPrice(item.totalPrice || 0),
      }));
    }

    if (orderData) {
      const orderInfo = orderData.order || orderData;
      const invoiceInfo = orderData.invoice || {};

      if (isPartsOrder() && (orderInfo.items || invoiceInfo.items)) {
        // One row per physical piece is how parts orders are stored — the saw
        // needs a work order per cut. That's unreadable as a line list: a six
        // box order runs to 108 rows. Group identical parts into one line and
        // carry the cut lengths inside it, so nothing is hidden.
        const rawItems = (orderInfo.items || invoiceInfo.items || []) as any[];
        const groups = new Map<string, any>();
        for (const it of rawItems) {
          // Include length so different cut lengths show as SEPARATE line items
          // (e.g. U-Channel 144" ×4 and 107" ×4), not one lumped "× 8" with a
          // confusing "Cuts: 144, 107". Fabric (no lengthInches) groups as before.
          const key = [it.sku || it.name || 'item', it.color || '', it.lengthInches ?? ''].join('|');
          const qty = Number(it.quantity) || 1;
          const price = Number(it.unitPrice ?? it.totalPrice ?? 0) || 0;
          const existing = groups.get(key);
          if (existing) {
            existing.quantity += qty;
            existing.totalPrice += price * qty;
            if (price !== existing.firstPrice) existing.mixedPricing = true;
            if (it.lengthInches) existing.lengths.push(Number(it.lengthInches));
          } else {
            groups.set(key, {
              ...it,
              quantity: qty,
              totalPrice: price * qty,
              firstPrice: price,
              mixedPricing: false,
              lengths: it.lengthInches ? [Number(it.lengthInches)] : [],
            });
          }
        }

        // Tape-measure fractions — a cutter reading 186.4375" has to convert it.
        const toFraction = (v: number): string => {
          const whole = Math.floor(v);
          let n = Math.round((v - whole) * 16);
          if (n === 0) return `${whole}"`;
          let d = 16;
          while (n % 2 === 0) { n /= 2; d /= 2; }
          return `${whole} ${n}/${d}"`;
        };

        return [...groups.values()].map((item: any, index: number) => {
          // Build a "size" string from whatever dimensions the cart captured.
          // Fabric items (newer cart code) save width + height. U-channel items
          // save lengthInches. Description is the cart's pre-formatted summary
          // ("48" W × 96" H (seam) + 2 windows") when present.
          const w = item.width;
          const h = item.height;
          const sizeParts: string[] = [];
          // For cut parts (u-channel etc.) the raw description carries the
          // order-time note "(Factory +6")", which misreads as "add 6 → cut
          // 150". The +6 is ALREADY baked into lengthInches, so the clean
          // "Cut 144"" note below is the cut — skip the raw description here.
          if (item.lengths?.length) {
            // cut part — size comes from the Cut note below
          } else if (item.description) sizeParts.push(item.description);
          else if (w && h) sizeParts.push(`${w}" W × ${h}" H`);
          else if (item.lengthInches) sizeParts.push(`${item.lengthInches}" length`);
          const noteParts: string[] = [];
          if (item.sku) noteParts.push(`SKU: ${item.sku}`);
          if (sizeParts.length > 0) noteParts.push(sizeParts.join(' · '));
          // Every cut length, so grouping never conceals what's being bought.
          if (item.lengths?.length) {
            noteParts.push(item.lengths.length === 1
              ? `Cut ${toFraction(item.lengths[0])}`
              : `Cuts: ${item.lengths.map(toFraction).join(', ')}`);
          }
          // Cut parts are priced per piece, so pieces of different lengths in
          // one line have different unit prices. Say so rather than printing a
          // unit price that doesn't multiply up to the line total.
          if (item.mixedPricing) noteParts.push('priced per length');
          return {
            id: `item-${index}`,
            lineNumber: index + 1,
            productType: item.type || 'part',
            fabricType: item.name,
            fabricColor: item.color,
            // Average for mixed-length groups so unit x qty reconciles to the
            // line total; exact where every piece is the same price.
            unitPrice: toDealerPrice(
              item.mixedPricing && item.quantity
                ? item.totalPrice / item.quantity
                : (item.unitPrice || 0),
            ),
            totalPrice: toDealerPrice(item.totalPrice || 0),
            quantity: item.quantity,
            notes: noteParts.join(' — '),
            lengthInches: item.lengthInches,
            exactLength: item.exactLength,
            width: w,
            height: h,
            description: item.description,
          };
        });
      }

      const screens = invoiceInfo.screens || [];
      const units = orderInfo.units || [];

      if (screens.length > 0 || units.length > 0) {
        return screens.map((screen: any, index: number) => {
          const unit = units[index] || {};
          const msrpUnitPrice = screen.basePrice || 0;
          const msrpUChannelPrice = screen.uChannelCost || 0;
          return {
            id: `screen-${index}`,
            lineNumber: index + 1,
            productType: screen.productType || 'screen',
            widthTop: unit.widthTop || screen.width,
            widthMiddle: unit.widthMiddle || screen.width,
            widthBottom: unit.widthBottom || screen.width,
            heightLeft: unit.heightLeft || screen.height,
            heightCenter: unit.heightCenter || screen.height,
            heightRight: unit.heightRight || screen.height,
            fabricType: orderInfo.sameFabricForAll
              ? orderInfo.globalFabricType
              : (unit.fabricType || screen.fabricType),
            fabricColor: orderInfo.sameFabricForAll
              ? orderInfo.globalFabricColor
              : (unit.fabricColor || screen.fabricColor),
            motorSide: unit.motorSide || 'right',
            uChannel: orderInfo.sameUchannel
              ? orderInfo.globalUChannel
              : (unit.uChannel || 'none'),
            biasedCut: unit.biasedCut || false,
            biasedCutInches: unit.biasedCutInches || 0,
            installationType: orderInfo.sameInstallationForAll
              ? orderInfo.globalInstallationType
              : (unit.installationType || 'wall'),
            trackMount: orderInfo.sameTrackMount
              ? orderInfo.globalTrackMount
              : (unit.trackMount || 'face'),
            recessed: orderInfo.sameRecessed
              ? orderInfo.globalRecessed
              : (unit.recessed || false),
            brushSize: unit.brushSize || orderInfo.brushSize,
            windowPanel: unit.windowPanel || false,
            windowOrientation: unit.windowOrientation,
            windowCount: unit.windowCount,
            windowWidth: unit.windowWidth,
            windowHeight: unit.windowHeight,
            topBorderInches: unit.topBorderInches,
            sideBorderInches: unit.sideBorderInches,
            bottomBorderInches: unit.bottomBorderInches,
            screenLocation: unit.screenLocation || unit.location,
            // Apply dealer discount - show dealer price, not MSRP
            unitPrice: toDealerPrice(msrpUnitPrice),
            uChannelPrice: toDealerPrice(msrpUChannelPrice),
            totalPrice: toDealerPrice(msrpUnitPrice + msrpUChannelPrice),
            status: 'pending',
          };
        });
      }
    }

    return [];
  };

  const displayItems = getDisplayItems();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {error || 'Order not found'}
        <Link to="/orders" className="ml-4 underline">
          Back to Orders
        </Link>
      </div>
    );
  }

  // Determine shipping method early for progress step logic
  const orderInfo = orderData?.order || orderData || {};
  const shippingMethod = (order as any).shippingMethod || orderInfo.shippingMethod || 'freight';
  const isWillCall = shippingMethod === 'willcall' || shippingMethod === 'will_call';
  const statusSteps = isPartsOrder() ? partsOrderStatusSteps : isWillCall ? willCallStatusSteps : freightStatusSteps;
  const currentStep = statusSteps.indexOf(order.status);

  // Get data from various sources
  const rawShippingAddress = (order as any).shipToAddress || (order as any).shippingAddress || (order as any).ship_to_address;
  let parsedAddress: any = null;

  if (rawShippingAddress) {
    try {
      parsedAddress = typeof rawShippingAddress === 'string' ? JSON.parse(rawShippingAddress) : rawShippingAddress;
    } catch {
      parsedAddress = null;
    }
  }

  if (!parsedAddress && orderData) {
    const orderInfo = orderData.order || orderData;
    if (orderInfo.shipStreet || orderInfo.shipCity) {
      parsedAddress = {
        street: orderInfo.shipStreet,
        city: orderInfo.shipCity,
        state: orderInfo.shipState,
        zip: orderInfo.shipZip,
        name: orderInfo.contactName,
      };
    }
  }

  const invoiceInfo = orderData?.invoice || {};

  // Customer info from order data
  const customerName = orderInfo.customerName || orderInfo.customer_name || (order as any).customerName;
  const customerPhone = orderInfo.customerPhone || orderInfo.customer_phone || (order as any).customerPhone;
  const customerEmail = orderInfo.customerEmail || orderInfo.customer_email || (order as any).customerEmail;
  const customerStreet = orderInfo.customerStreet || orderInfo.customer_street || (order as any).customerStreet;
  const customerCity = orderInfo.customerCity || orderInfo.customer_city || (order as any).customerCity;
  const customerState = orderInfo.customerState || orderInfo.customer_state || (order as any).customerState;
  const customerZip = orderInfo.customerZip || orderInfo.customer_zip || (order as any).customerZip;

  // Controls & Accessories
  const motorType = orderInfo.motorType || (order as any).motorType || (order as any).motor_type || 'Alpha';
  const remoteTypeRaw = orderInfo.remoteType || orderInfo.controllerType || (order as any).remoteType || (order as any).remote_type || 'none';
  const remoteType = remoteTypeRaw;
  const remoteTypeDisplay = formatRemoteType(remoteTypeRaw);
  const remoteQty = orderInfo.remoteQty ?? orderInfo.controllerQty ?? (order as any).remoteQty ?? (order as any).remote_qty ?? 0;
  const wallSwitchQty = orderInfo.wallSwitchQty || (order as any).wallSwitchQty || 0;

  // Shipping options
  const liftgateDelivery = orderInfo.liftgateDelivery || (order as any).liftgateDelivery;
  const residentialDelivery = orderInfo.residentialDelivery || (order as any).residentialDelivery;
  const insideDelivery = orderInfo.insideDelivery || (order as any).insideDelivery;

  // Order info fields
  const orderedBy = orderInfo.orderedBy || orderInfo.contactName || (order as any).orderedBy || (order as any).contactName;
  const authorizedBy = orderInfo.authorizedBy || (order as any).authorizedBy || orderedBy;
  const neededByDate = (order as any).neededByDate || orderInfo.neededByDate;

  // Comments
  const comments = order.notes || (order as any).comments || orderInfo.comments || orderInfo.notes;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-3">
            <Link to="/orders" className="text-gray-400 hover:text-white">
              &larr; Orders
            </Link>
          </div>
          <h1 className="text-2xl font-bold text-white mt-2">
            Order {order.orderNumber || (order as any).order_number}
          </h1>
          <p className="text-gray-400">{order.sidemark || '-'}</p>
          {isPartsOrder() && (
            <span className="inline-block mt-1 px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
              Parts Order
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[order.status] || 'bg-gray-600 text-white'}`}>
            {(() => {
              // Will-call uses same 'shipped' code as freight, but for dealers
              // "Shipped" reads wrong when they're picking up. Relabel only.
              const sm = String((order as any).shippingMethod || (order as any).shipping_method || '').toLowerCase();
              const isWillCall = sm === 'will_call' || sm === 'willcall';
              if (isWillCall && order.status === 'shipped') return 'Ready for Pickup';
              return statusLabels[order.status] || order.status;
            })()}
          </span>
          {order.status === 'draft' && (
            <Link
              to={`/orders/${order.id}/edit?action=pay`}
              className="bg-keder-yellow hover:bg-yellow-300 text-black px-4 py-2 rounded-lg text-sm font-semibold transition-colors shadow"
            >
              Submit &amp; Pay →
            </Link>
          )}
          {['draft', 'submitted', 'pending_payment', 'needs_correction'].includes(order.status) && (
            <Link
              to={`/orders/${order.id}/edit`}
              className="border border-keder-yellow text-keder-yellow hover:bg-keder-yellow hover:text-black px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              {order.status === 'needs_correction' ? 'Correct & Resubmit' : 'Edit Order'}
            </Link>
          )}
          {/* Payment buttons are in the Invoice section below — scroll down */}
          {['draft', 'pending_payment', 'submitted'].includes(order.status) && (
            <button
              onClick={handleCancelOrder}
              disabled={submitting}
              className="border border-red-500 text-red-500 hover:bg-red-500 hover:text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              {order.status === 'draft' ? 'Delete' : 'Cancel Order'}
            </button>
          )}
          {!order.paid_in_full && !order.paidInFull && !['draft', 'cancelled'].includes(order.status) && (
            <button
              onClick={() => {
                const url = `${window.location.origin}/orders/${order.id}`;
                navigator.clipboard.writeText(url).then(() => {
                  alert(`Order link copied!\n\n${url}\n\nShare with your accountant — they need a portal login to access it.\n\nAdd them as a portal user under Admin → Team Management → Portal Users.`);
                });
              }}
              className="border border-green-500 text-green-500 hover:bg-green-500 hover:text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              Share Order Link
            </button>
          )}
          {!isPartsOrder() && !['draft'].includes(order.status) && displayItems.length > 0 && (
            <button
              onClick={() => openAllInstallerSheets(order.id, displayItems.length)}
              className="border border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print Installer Sheets ({displayItems.length})
            </button>
          )}
          {!isPartsOrder() && !['cancelled', 'closed', 'lost', 'install_scheduled', 'installed', 'warranty_registered'].includes(order.status) && (
            <button
              onClick={() => setShowScheduleInstallModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Schedule Install
            </button>
          )}
          {!isPartsOrder() && order.status === 'install_scheduled' && (
            <>
              <button
                onClick={() => {
                  setInstallForm({
                    ...installForm,
                    installDate: (order as any).installScheduledDate || '',
                    installTime: (order as any).installScheduledTime || '',
                    notes: (order as any).installNotes || '',
                  });
                  setShowScheduleInstallModal(true);
                }}
                className="border border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Reschedule
              </button>
              <button
                onClick={() => setShowMarkInstalledModal(true)}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Mark Installed
              </button>
            </>
          )}
          {order.status === 'installed' && (
            <Link
              to={`/warranty/register?orderId=${order.id}`}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Register Warranty
            </Link>
          )}
        </div>
      </div>

      {/* Why the invoice changed — final shipping adjusted / order corrected / etc. */}
      {invoiceChanges.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <svg className="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg>
            <h3 className="text-sm font-semibold text-amber-300">Invoice updated</h3>
          </div>
          <ul className="space-y-1.5">
            {invoiceChanges.map((c, i) => (
              <li key={i} className="flex items-center justify-between text-sm">
                <span className="text-amber-100/90">
                  {c.reason}
                  <span className="text-amber-200/50 ml-2 text-xs">{new Date(c.date).toLocaleDateString()}</span>
                </span>
                <span className={`font-semibold ${c.delta > 0 ? 'text-red-400' : 'text-green-400'}`}>
                  {c.delta > 0 ? '+' : '−'}{formatCurrency(Math.abs(c.delta))}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-amber-200/50 mt-2">Your order total was adjusted after it was placed. See the invoice below for the current breakdown.</p>
        </div>
      )}

      {/* Draft Banner — this order hasn't been submitted yet, no work is happening on it */}
      {order.status === 'draft' && (
        <div style={{ background: 'rgba(234, 179, 8, 0.15)', border: '1px solid rgba(234, 179, 8, 0.5)', borderRadius: '8px', padding: '16px', marginBottom: '8px' }}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 flex-1">
              <svg className="w-6 h-6 text-keder-yellow flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <p className="text-keder-yellow font-semibold text-lg">This order is still a Draft</p>
                <p className="text-gray-200 mt-1 text-sm">
                  We haven't received it yet — no factory work has started and no payment has been taken. Click <strong>Submit &amp; Pay</strong> to send it to the factory.
                </p>
              </div>
            </div>
            <Link
              to={`/orders/${order.id}/edit?action=pay`}
              className="bg-keder-yellow hover:bg-yellow-300 text-black px-5 py-2.5 rounded-lg text-sm font-bold transition-colors whitespace-nowrap shadow flex items-center gap-2"
            >
              Submit &amp; Pay →
            </Link>
          </div>
        </div>
      )}

      {/* Correction Required Banner */}
      {order.status === 'needs_correction' && (
        <div style={{ background: 'rgba(234, 88, 12, 0.15)', border: '1px solid rgba(234, 88, 12, 0.4)', borderRadius: '8px', padding: '16px', marginBottom: '8px' }}>
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-orange-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <p className="text-orange-300 font-semibold text-lg">Correction Required</p>
              <p className="text-orange-200 mt-1">
                {(order as any).correctionReason || (order as any).correction_reason || 'The factory has sent this order back for correction.'}
              </p>
              <p className="text-orange-400/60 text-xs mt-2">
                {((order as any).correctionRequestedAt || (order as any).correction_requested_at) &&
                  `Requested ${new Date((order as any).correctionRequestedAt || (order as any).correction_requested_at).toLocaleString()}`
                }
              </p>
              <Link
                to={`/orders/${order.id}/edit`}
                className="inline-block mt-3 bg-keder-yellow text-black px-4 py-2 rounded-lg text-sm font-semibold hover:bg-yellow-400 transition-colors"
              >
                Correct &amp; Resubmit Order
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Tools Row */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => {
            // Pass order screens to Price Builder
            // Extract width/height from displayItems — handles both camelCase (API) and snake_case (raw) fields
            let screenData = displayItems.map((item: any) => {
              const w = Number(item.widthTop) || Number(item.width_top) || Number(item.width) || 0;
              const h = Number(item.heightLeft) || Number(item.height_left) || Number(item.height) || 0;
              const uc = (item.uChannel || item.u_channel || 'none') !== 'none';
              return { width: w, height: h, uChannel: uc };
            }).filter(s => s.width > 0 && s.height > 0);

            // Fallback: if displayItems had no valid screens, try order_data JSONB directly
            if (screenData.length === 0 && orderData) {
              const orderInfo = orderData.order || orderData;
              const units = orderInfo.units || [];
              if (units.length > 0) {
                screenData = units.map((u: any) => ({
                  width: Number(u.widthTop) || Number(u.width) || 0,
                  height: Number(u.heightLeft) || Number(u.height) || 0,
                  uChannel: (u.uChannel || 'none') !== 'none',
                })).filter((s: any) => s.width > 0 && s.height > 0);
              }
            }

            sessionStorage.setItem('orderToPriceBuilder', JSON.stringify({
              orderId: order.id,
              orderNumber: order.orderNumber || (order as any).order_number,
              screens: screenData,
              customerInfo: {
                name: (order as any).customerName || (order as any).customer_name || '',
                email: (order as any).customerEmail || (order as any).customer_email || '',
                phone: (order as any).customerPhone || (order as any).customer_phone || '',
              },
            }));
            navigate('/price-builder');
          }}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-yellow-50 text-gray-700 rounded-lg text-sm font-medium transition-colors border border-gray-200 hover:border-yellow-400"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
          </svg>
          {((order as any).customerSellPrice || (order as any).customer_sell_price) > 0 ? 'Edit Estimate' : 'Price Builder'}
        </button>
        <button
          onClick={() => {
            // Pass order context to Screen Designer
            sessionStorage.setItem('orderToDesigner', JSON.stringify({
              orderId: order.id,
              orderNumber: order.orderNumber || (order as any).order_number,
            }));
            navigate('/designer');
          }}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-yellow-50 text-gray-700 rounded-lg text-sm font-medium transition-colors border border-gray-200 hover:border-yellow-400"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
          </svg>
          Screen Visualizer
        </button>
      </div>

      {/* Linked Screen Designs */}
      {linkedDesigns.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-900">
            Screen Designs ({linkedDesigns.length} opening{linkedDesigns.length !== 1 ? 's' : ''})
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {linkedDesigns.map((design: any) => (
              <div
                key={design.id}
                className="cursor-pointer group"
                onClick={() => setExpandedDesign(expandedDesign === design.id ? null : design.id)}
              >
                <div className="relative rounded-lg overflow-hidden border border-gray-200 group-hover:border-yellow-400 transition-colors">
                  <img
                    src={design.image_url}
                    alt={design.name}
                    className="w-full h-32 object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                    <span className="text-white text-xs font-medium">
                      {design.screen_count || (design.screens ? JSON.parse(typeof design.screens === 'string' ? design.screens : JSON.stringify(design.screens)).length : 0)} screen{(design.screen_count || 0) !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1 truncate">{design.name}</p>
              </div>
            ))}
          </div>
          {/* Expanded view */}
          {expandedDesign && linkedDesigns.find((d: any) => d.id === expandedDesign) && (
            <div className="mt-4 border-t pt-4">
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-md font-medium text-gray-800">
                  {linkedDesigns.find((d: any) => d.id === expandedDesign)!.name}
                </h3>
                <button
                  onClick={() => setExpandedDesign(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <img
                src={linkedDesigns.find((d: any) => d.id === expandedDesign)!.image_url}
                alt="Design"
                className="w-full max-w-2xl rounded-lg border border-gray-200"
              />
              {linkedDesigns.find((d: any) => d.id === expandedDesign)!.notes && (
                <p className="text-sm text-gray-600 mt-2">
                  {linkedDesigns.find((d: any) => d.id === expandedDesign)!.notes}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Consolidated shipment banner — only when this order is grouped */}
      {(order as any).shipmentId && (
        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-4 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-purple-900 dark:text-purple-200 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
              Part of a consolidated shipment
            </div>
            <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">
              This order ships together with others. Your freight share for this order:&nbsp;
              <span className="font-mono font-semibold">
                {(order as any).freightShare != null
                  ? '$' + Number((order as any).freightShare).toFixed(2)
                  : '—'}
              </span>
            </p>
          </div>
          <Link
            to={`/shipments`}
            className="text-xs text-purple-700 dark:text-purple-300 underline hover:text-purple-900 dark:hover:text-purple-100 whitespace-nowrap"
          >
            View shipment
          </Link>
        </div>
      )}

      {/* Progress Tracker */}
      {!['draft', 'cancelled', 'on_hold', 'pending_payment'].includes(order.status) && currentStep >= 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-900">Order Progress</h2>
          <div className="relative">
            <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200" />
            <div
              className="absolute top-5 left-0 h-0.5 bg-yellow-500 transition-all duration-500"
              style={{ width: `${(currentStep / (statusSteps.length - 1)) * 100}%` }}
            />
            <div className="relative flex justify-between">
              {statusSteps.map((step, index) => {
                const isComplete = index <= currentStep;
                const isCurrent = index === currentStep;
                return (
                  <div key={step} className="flex flex-col items-center">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                        isComplete
                          ? 'bg-yellow-500 text-black'
                          : 'bg-gray-200 text-gray-500'
                      } ${isCurrent ? 'ring-2 ring-yellow-500 ring-offset-2' : ''}`}
                    >
                      {isComplete ? '✓' : index + 1}
                    </div>
                    <span className={`mt-2 text-xs ${isComplete ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                      {isWillCall ? (willCallLabels[step] || statusLabels[step]) : statusLabels[step]}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Contextual next-step prompts so dealers can advance the order
                from the progress tracker itself, instead of hunting for
                Schedule Install / Mark Installed / Register Warranty buttons
                in the top action bar. */}
            {!isPartsOrder() && ['shipped', 'delivered'].includes(order.status) && (
              <div className="mt-6 pt-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Next step: Schedule Installation</p>
                  <p className="text-xs text-gray-500">Set an install date so the order can advance toward completion.</p>
                </div>
                <button
                  onClick={() => setShowScheduleInstallModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 self-start sm:self-auto"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Schedule Install
                </button>
              </div>
            )}
            {!isPartsOrder() && order.status === 'install_scheduled' && (
              <div className="mt-6 pt-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Next step: Mark as Installed</p>
                  <p className="text-xs text-gray-500">
                    Install scheduled
                    {(order as any).installScheduledDate ? ` for ${new Date((order as any).installScheduledDate).toLocaleDateString()}` : ''}
                    . Mark installed once complete.
                  </p>
                </div>
                <div className="flex gap-2 self-start sm:self-auto">
                  <button
                    onClick={() => {
                      setInstallForm({
                        ...installForm,
                        installDate: (order as any).installScheduledDate || '',
                        installTime: (order as any).installScheduledTime || '',
                        notes: (order as any).installNotes || '',
                      });
                      setShowScheduleInstallModal(true);
                    }}
                    className="border border-blue-500 text-blue-600 hover:bg-blue-50 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Reschedule
                  </button>
                  <button
                    onClick={() => setShowMarkInstalledModal(true)}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Mark Installed
                  </button>
                </div>
              </div>
            )}
            {order.status === 'installed' && (
              <div className="mt-6 pt-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Next step: Register Warranty</p>
                  <p className="text-xs text-gray-500">Register the warranty to close out this order.</p>
                </div>
                <Link
                  to={`/warranty/register?orderId=${order.id}`}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 self-start sm:self-auto"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  Register Warranty
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Production Progress Bar — once the order is shipped/delivered, show
          100% regardless of orphan WO state. The shipping team often advances
          status on the order without scanning every individual unit through
          the late shipping stations (SHIPLABEL/SHIPBOX/SHIPSTRAP/SHIPPACK), so
          the raw count under-reports completion for the dealer view. */}
      {productionProgress && productionProgress.totalSteps > 0 && (() => {
        const orderDone = ['shipped', 'delivered', 'install_scheduled', 'installed', 'warranty_registered', 'completed', 'closed'].includes(order.status);
        const displayPercent = orderDone ? 100 : productionProgress.progressPercent;
        const displayDone = orderDone ? productionProgress.totalSteps : productionProgress.completedSteps;
        return (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900">Production Progress</h2>
              <span className="text-2xl font-bold text-yellow-600">{displayPercent}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500 bg-yellow-500"
                style={{ width: `${displayPercent}%` }}
              />
            </div>
            <div className="flex justify-between mt-2 text-sm text-gray-600">
              <span>{displayDone} of {productionProgress.totalSteps} stations complete</span>
              {!orderDone && productionProgress.inProgressSteps > 0 && (
                <span className="text-yellow-600 font-medium">{productionProgress.inProgressSteps} in progress</span>
              )}
            </div>
          </div>
        );
      })()}

      {/* Pre-production status */}
      {productionProgress && productionProgress.totalSteps === 0 && ['pending_release', 'approved', 'confirmed'].includes(order.status) && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Production Progress</h2>
              <p className="text-sm text-gray-600">Your order has been approved and is queued for production</p>
            </div>
          </div>
        </div>
      )}

      {/* Shortage Hold Banner */}
      {shortageInfo && shortageInfo.onHold && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg shadow p-6">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-amber-800">On Hold - Awaiting Parts</h3>
              <p className="text-sm text-amber-700 mt-1">
                This order is temporarily on hold while we wait for parts to arrive. We are working to get it back on track as quickly as possible.
              </p>
              {shortageInfo.holdSince && (
                <p className="text-xs text-amber-600 mt-1">
                  On hold since {new Date(shortageInfo.holdSince).toLocaleDateString()}
                </p>
              )}
              {shortageInfo.items && shortageInfo.items.length > 0 && (
                <div className="mt-3">
                  <h4 className="text-sm font-medium text-amber-800 mb-2">Items awaiting arrival:</h4>
                  <ul className="space-y-1">
                    {shortageInfo.items.map((item, idx) => (
                      <li key={idx} className="flex items-center justify-between text-sm bg-amber-100 rounded px-3 py-2">
                        <span className="text-amber-900 font-medium">{item.description}</span>
                        {item.estimatedLeadTimeDays > 0 && (
                          <span className="text-amber-700 text-xs ml-4 whitespace-nowrap">
                            ~{item.estimatedLeadTimeDays} day{item.estimatedLeadTimeDays !== 1 ? 's' : ''} estimated
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Customer Details Section */}
      {(customerName || customerPhone || customerEmail || customerStreet) && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-900">Customer Details</h2>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            {customerName && (
              <div>
                <dt className="text-gray-500">Customer Name</dt>
                <dd className="font-medium text-gray-900">{customerName}</dd>
              </div>
            )}
            {customerPhone && (
              <div>
                <dt className="text-gray-500">Phone</dt>
                <dd className="font-medium text-gray-900">{customerPhone}</dd>
              </div>
            )}
            {customerEmail && (
              <div>
                <dt className="text-gray-500">Email</dt>
                <dd className="font-medium text-gray-900">{customerEmail}</dd>
              </div>
            )}
            {customerStreet && (
              <div className="md:col-span-2">
                <dt className="text-gray-500">Address</dt>
                <dd className="font-medium text-gray-900">
                  {customerStreet}
                  {customerCity && `, ${customerCity}`}
                  {customerState && `, ${customerState}`}
                  {customerZip && ` ${customerZip}`}
                </dd>
              </div>
            )}
          </dl>
        </div>
      )}

      {/* Main Grid — was 3 cols (Order Info / Controls / Shipping); now just
          Shipping remains so collapsing to a single full-width row. */}
      <div className="grid grid-cols-1 gap-6">
        {/* "Order Information" and "Controls & Accessories" cards removed —
            both duplicated data already shown in the invoice header (order #,
            sidemark, date) and accessories table (motor, remote, wall switch).
            Shipping card stays since it's the only place the will-call pickup
            address surfaces on this page. */}

        {/* "Added Accessories" card removed — was exact duplicate of the
            invoice's Accessories & Add-ons table below. Same data, same
            dollar amounts, same total. Invoice section is the source of truth. */}

        {/* Shipping — hidden for will-call orders since the invoice's Ship To
            block already shows "Will Call Pickup / 12446 State Hwy 205 / Lavon,
            TX 75166" and there's no carrier / freight cost / tracking to add.
            For real shipping orders (LTL freight or parcel) this card stays
            visible because it carries carrier name, freight cost, freight
            options badges, and clickable tracking — none of which fit on the
            invoice header. */}
        {!(shippingMethod === 'willcall' || shippingMethod === 'will_call') && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-900">Shipping</h2>
          <div className="space-y-3">
            <div>
              <span className="text-sm text-gray-500">Method</span>
              <p className="font-medium text-gray-900">{formatShippingMethod(shippingMethod)}</p>
            </div>

            {/* Carrier info for freight orders */}
            {((order as any).selectedCarrierName || (order as any).selected_carrier_name) && (
              <div>
                <span className="text-sm text-gray-500">Carrier</span>
                <p className="font-medium text-gray-900">{(order as any).selectedCarrierName || (order as any).selected_carrier_name}</p>
              </div>
            )}

            {((order as any).freightTotalAmount || (order as any).freight_total_amount) > 0 && (
              <div>
                <span className="text-sm text-gray-500">Freight Cost</span>
                <p className="font-medium text-gray-900">{formatCurrency((order as any).freightTotalAmount || (order as any).freight_total_amount)}</p>
              </div>
            )}

            {/* Freight is an estimate ONLY until the shipment is booked. Once a
                PRO / BOL / tracking number exists the carrier is booked and the
                rate is final (e.g. VT26060012: ready_to_ship but PRO+BOL issued —
                $649.14 is the final charge, not an estimate). Also hide once
                shipped/delivered/etc. */}
            {(() => {
              const booked = !!(
                (order as any).freightProNumber || (order as any).freight_pro_number ||
                (order as any).freightBolNumber || (order as any).freight_bol_number ||
                (order as any).trackingNumber || (order as any).tracking_number ||
                (order as any).parcelTrackingNumber || (order as any).parcel_tracking_number
              );
              const finalStatus = ['shipped', 'delivered', 'completed', 'closed', 'cancelled'].includes(order.status);
              return !isWillCall && !booked && !finalStatus ? <ShippingDisclaimer className="mt-1" /> : null;
            })()}

            {shippingMethod === 'willcall' || shippingMethod === 'will_call' ? (
              <div className="text-sm">
                <address className="not-italic text-gray-700">
                  <div>International Shade Products</div>
                  <div>12446 State Hwy 205</div>
                  <div>Lavon, TX 75166</div>
                </address>
                <p className="mt-2 text-gray-500 text-xs">Mon-Fri 8am-5pm CT</p>
              </div>
            ) : parsedAddress ? (
              <address className="text-sm not-italic text-gray-700">
                {(parsedAddress.name || parsedAddress.contactName) && (
                  <div className="font-medium">{parsedAddress.name || parsedAddress.contactName}</div>
                )}
                {(parsedAddress.address1 || parsedAddress.street) && (
                  <div>{parsedAddress.address1 || parsedAddress.street}</div>
                )}
                {parsedAddress.address2 && <div>{parsedAddress.address2}</div>}
                <div>
                  {parsedAddress.city}, {parsedAddress.state} {parsedAddress.zip || parsedAddress.postalCode}
                </div>
              </address>
            ) : (
              <p className="text-sm text-gray-500">No address provided</p>
            )}

            {/* Freight Options as Badges */}
            {(liftgateDelivery || residentialDelivery || insideDelivery) && (
              <div className="pt-3 border-t">
                <span className="text-sm text-gray-500 block mb-2">Freight Options</span>
                <div className="flex flex-wrap gap-2">
                  {liftgateDelivery && (
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      Liftgate Required
                    </span>
                  )}
                  {residentialDelivery && (
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                      Residential Delivery
                    </span>
                  )}
                  {insideDelivery && (
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                      Inside Delivery
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Tracking Info — inline in shipping card */}
            {(() => {
              const freightTracking = (order as any).trackingNumber || (order as any).tracking_number;
              const parcelTracking = (order as any).parcelTrackingNumber || (order as any).parcel_tracking_number;
              const parcelCarrier = (order as any).parcelCarrier || (order as any).parcel_carrier;
              const parcelService = (order as any).parcelServiceName || (order as any).parcel_service_name;
              const freightPro = (order as any).freightProNumber || (order as any).freight_pro_number;
              const freightBol = (order as any).freightBolNumber || (order as any).freight_bol_number;
              const freightTrackingUrl = (order as any).freightTrackingUrl || (order as any).freight_tracking_url || null;
              const trackingNum = parcelTracking || freightTracking;
              // Freight carriers often issue only a PRO or BOL (no parcel-style
              // tracking number), so still render if we have either of those.
              if (!trackingNum && !freightPro && !freightBol) return null;

              const getTrackingUrl = (carrier: string, tracking: string) => {
                if (!carrier || !tracking) return null;
                const c = carrier.toLowerCase();
                if (c === 'ups') return `https://www.ups.com/track?tracknum=${tracking}`;
                if (c === 'fedex') return `https://www.fedex.com/fedextrack/?trknbr=${tracking}`;
                if (c === 'usps') return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${tracking}`;
                return null;
              };

              const trackingUrl = parcelTracking ? getTrackingUrl(parcelCarrier || 'ups', parcelTracking) : null;
              const displayCarrier = parcelTracking
                ? `${(parcelCarrier || 'UPS').toUpperCase()}${parcelService ? ` ${parcelService}` : ''}`
                : null;

              return (
                <div className="pt-3 border-t">
                  {displayCarrier && (
                    <div className="mb-2">
                      <span className="text-sm text-gray-500">Carrier</span>
                      <p className="font-medium text-gray-900">{displayCarrier}</p>
                    </div>
                  )}
                  {trackingNum && (
                    <div className="mb-2">
                      <span className="text-sm text-gray-500">Tracking Number</span>
                      <p className="font-medium">
                        {trackingUrl ? (
                          <a href={trackingUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline flex items-center gap-1">
                            {trackingNum}
                            <svg className="w-3.5 h-3.5 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                          </a>
                        ) : (
                          <span className="text-gray-900">{trackingNum}</span>
                        )}
                      </p>
                    </div>
                  )}
                  {freightPro && (
                    <div className="mb-2">
                      <span className="text-sm text-gray-500">PRO Number</span>
                      <p className="font-medium">
                        {freightTrackingUrl ? (
                          <a href={freightTrackingUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline inline-flex items-center gap-1">
                            {freightPro}
                            <svg className="w-3.5 h-3.5 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                          </a>
                        ) : (
                          <span className="text-gray-900">{freightPro}</span>
                        )}
                      </p>
                    </div>
                  )}
                  {freightBol && (
                    <div className="mb-2">
                      <span className="text-sm text-gray-500">BOL Number</span>
                      <p className="font-medium">
                        {freightTrackingUrl ? (
                          <a href={freightTrackingUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline inline-flex items-center gap-1">
                            {freightBol}
                            <svg className="w-3.5 h-3.5 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                          </a>
                        ) : (
                          <span className="text-gray-900">{freightBol}</span>
                        )}
                      </p>
                    </div>
                  )}
                  {(freightPro || freightBol) && (
                    <p className="text-xs text-gray-500 mb-2">
                      {freightTrackingUrl
                        ? <>Click the PRO{freightBol ? ' or BOL' : ''} number above to track with {(order as any).selectedCarrierName || (order as any).selected_carrier_name || 'the carrier'}.</>
                        : <>Track your shipment with the carrier using the PRO{freightBol ? ' or BOL' : ''} number above.</>}
                    </p>
                  )}
                  {((order as any).shippedAt || (order as any).shipped_at) && (
                    <div>
                      <span className="text-sm text-gray-500">Shipped</span>
                      <p className="font-medium text-gray-900">{formatDate((order as any).shippedAt || (order as any).shipped_at)}</p>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
        )}
      </div>

      {/* Order Summary moved into DealerInvoiceView via the summaryAside prop
          (below) so it sits next to the Totals + payment buttons block instead
          of as a separate full-width band above. Same data, same fields. */}

      {/* New order confirmation banner — shows while order is submitted but
          unpaid OR right after submit (via fromNewOrder route state). Persists
          across reloads now, not just first-visit. */}
      {(fromNewOrder || (order.status === 'submitted' && !((order as any).paymentAmount || (order as any).payment_amount))) && (
        <div className="bg-green-50 border-2 border-green-400 rounded-xl p-5 flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-green-800 text-lg">Order Confirmed!</h3>
            <p className="text-green-700 mt-1">
              Your order has been submitted. Review your invoice and production details below, then proceed to payment using the "Pay with Card" or "Bank / ACH" buttons.
            </p>
          </div>
        </div>
      )}

      {/* Invoice Section — auto-generates if no saved invoice exists */}
      <div id="invoice-section">
        {order && !['draft', 'cancelled'].includes(order.status) && (
          <DealerInvoiceView
            order={order}
            onPayInvoice={handleSubmitOrder}
            onRefresh={fetchOrder}
            achDiscountPercent={achDiscountPercent}
            authorizeAchDiscountPercent={authorizeAchDiscountPercent}
            availableCredit={availableCredit}
            summaryAside={
              // Only render Your Sale / Your Profit on the left of the totals
              // when (a) the dealer has commission tracking enabled and (b) a
              // customer sell price has been recorded. Items / Accessories /
              // Shipping / Tax / Total are already in the invoice's right-column
              // breakdown.
              (trackingEnabled && ((order as any).customerSellPrice || (order as any).customer_sell_price) > 0) ? (
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-lg font-semibold mb-4 text-gray-900">Your Margin</h2>
                  <dl className="space-y-3 text-sm">
                    <div>
                      <dt className="text-gray-500">Your Sale</dt>
                      <dd className="font-bold text-green-600 text-xl">
                        {formatCurrency((order as any).customerSellPrice || (order as any).customer_sell_price)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Your Profit</dt>
                      <dd className="font-bold text-green-600 text-lg">
                        {formatCurrency(((order as any).customerSellPrice || (order as any).customer_sell_price) - (invoiceBalance !== null ? invoiceBalance : (order.totalAmount || (order as any).total_amount || 0)))}
                      </dd>
                    </div>
                  </dl>
                </div>
              ) : undefined
            }
          />
        )}
      </div>

      {/* Comments Section */}
      {comments && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-2 text-gray-900">Comments / Special Instructions</h2>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{comments}</p>
        </div>
      )}

      {/* Screen Units */}
      {displayItems.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">
              {isPartsOrder() ? 'Parts & Items' : 'Screen Units'}
            </h2>
          </div>
          <div className="divide-y">
            {isPartsOrder() ? (
              // Parts order items
              displayItems.map((item: any, index: number) => (
                <div key={item.id || index} className="p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="font-medium text-gray-900">#{index + 1} - {item.fabricType || item.name || 'Item'}</span>
                      {item.fabricColor && <span className="text-sm text-gray-500 ml-2">({item.fabricColor})</span>}
                    </div>
                    <div className="text-right">
                      <span className="text-sm text-gray-500">Qty: {item.quantity || 1}</span>
                      <span className="font-medium text-gray-900 ml-4">{formatCurrency(item.totalPrice || item.unitPrice || 0)}</span>
                    </div>
                  </div>
                  {item.lengthInches && (
                    <p className="text-sm text-gray-500 mt-1">Length: {item.lengthInches}" {item.exactLength ? '(Exact)' : '(Factory +6")'}</p>
                  )}
                </div>
              ))
            ) : (
              // Screen order items
              displayItems.map((item: any, index: number) => {
                const itemId = item.id || `item-${index}`;
                const isExpanded = expandedItems.has(itemId);
                const widthTop = item.widthTop || item.width_top || 0;
                const widthMid = item.widthMiddle || item.width_middle || widthTop;
                const widthBot = item.widthBottom || item.width_bottom || widthTop;
                const heightL = item.heightLeft || item.height_left || 0;
                const heightC = item.heightCenter || item.height_center || heightL;
                const heightR = item.heightRight || item.height_right || heightL;

                return (
                  <div key={itemId} className="border-b last:border-b-0">
                    {/* Collapsed Header */}
                    <div
                      className="p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => toggleItemExpand(itemId)}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-4">
                          <span className="font-semibold text-gray-900">Unit #{item.lineNumber || index + 1}</span>
                          {(item.expertMode || item.expert_mode) && (
                            <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-amber-100 text-amber-800">Expert</span>
                          )}
                          <span className="text-sm text-gray-600">
                            {Math.max(widthTop, widthMid, widthBot)}" × {Math.max(heightL, heightC, heightR)}"
                          </span>
                          <span className="text-sm text-gray-500">
                            {formatFabricType(item.fabricType || item.fabric_type)} - {item.fabricColor || item.fabric_color || '-'}
                          </span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded ${
                            item.status === 'complete' ? 'bg-green-100 text-green-800' :
                            item.status === 'in_production' ? 'bg-purple-100 text-purple-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {item.status || 'Pending'}
                          </span>
                          <span className="font-medium text-gray-900">{formatCurrency(item.totalPrice || item.unitPrice || 0)}</span>
                          <svg className={`w-5 h-5 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="px-4 pb-4 bg-gray-50">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                          {/* Visual Preview */}
                          <div>
                            <ScreenUnitPreview item={item} />
                          </div>

                          {/* Details */}
                          <div className="lg:col-span-2 space-y-4">
                            {/* Dimensions */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                              <h5 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">DIMENSIONS {(item.expertMode || item.expert_mode) ? <span className="text-amber-600">(Expert Mode)</span> : ''}</h5>
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                {(item.expertMode || item.expert_mode) ? (
                                  <>
                                    <div>
                                      <span className="text-gray-500">System Width:</span>
                                      <span className="ml-2 font-medium text-gray-900 dark:text-white">{widthTop}"</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-500">System Height:</span>
                                      <span className="ml-2 font-medium text-gray-900 dark:text-white">{heightL}"</span>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div>
                                      <span className="text-gray-500">Width:</span>
                                      <span className="ml-2 font-medium text-gray-900 dark:text-white">T: {widthTop}" | M: {widthMid}" | B: {widthBot}"</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-500">Height:</span>
                                      <span className="ml-2 font-medium text-gray-900 dark:text-white">L: {heightL}" | C: {heightC}" | R: {heightR}"</span>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Material */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                              <h5 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">MATERIAL</h5>
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="text-gray-500">Fabric Type:</span>
                                  <span className="ml-2 font-medium text-gray-900 dark:text-white">{formatFabricType(item.fabricType || item.fabric_type)}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Fabric Color:</span>
                                  <span className="ml-2 font-medium text-gray-900 dark:text-white">{item.fabricColor || item.fabric_color || '-'}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Frame Color:</span>
                                  <span className="ml-2 font-medium text-gray-900 dark:text-white">{item.systemColor || item.system_color || orderInfo.systemColor || 'Black'}</span>
                                </div>
                              </div>
                              {(item.componentColors || item.component_colors) && Object.keys(item.componentColors || item.component_colors).length > 0 && (() => {
                                const cc = item.componentColors || item.component_colors;
                                const fc = item.systemColor || item.system_color || '';
                                const isOpenRoll = String(item.systemType || item.system_type || '').toLowerCase() === 'open_roll';
                                const uChannel = String(item.uChannel || item.u_channel || 'none').toLowerCase();
                                const hasUChannel = uChannel !== 'none' && uChannel !== '';
                                const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
                                // Each part's ACTUAL color: its override, else the frame color.
                                const resolve = (override: string | undefined, ralKey: string) => {
                                  const color = override || fc;
                                  if (color === 'custom' && cc[ralKey]) return `Custom (${cc[ralKey]})`;
                                  return cap(color);
                                };
                                const parts: [string, string][] = [];
                                if (!isOpenRoll) parts.push(['Housing (Box)', resolve(cc.housingColor, 'housingRalNumber')]);
                                parts.push(['Weight Bar', resolve(cc.weightBarColor, 'weightBarRalNumber')]);
                                parts.push(['Left Track', resolve(cc.leftTrackColor, 'leftTrackRalNumber')]);
                                parts.push(['Right Track', resolve(cc.rightTrackColor, 'rightTrackRalNumber')]);
                                if (hasUChannel || cc.leftUChannelColor) parts.push(['Left U-Channel', resolve(cc.leftUChannelColor, 'leftUChannelRalNumber')]);
                                if (hasUChannel || cc.rightUChannelColor) parts.push(['Right U-Channel', resolve(cc.rightUChannelColor, 'rightUChannelRalNumber')]);
                                return (
                                  <div className="mt-2 p-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-300 dark:border-orange-600 rounded text-xs">
                                    <div className="font-semibold text-orange-700 dark:text-orange-400 mb-1">Component Colors (per part):</div>
                                    <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                                      {parts.map(([label, value]) => (
                                        <span key={label}><span className="text-gray-500 dark:text-gray-400">{label}:</span> <span className="font-medium">{value}</span></span>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>

                            {/* Installation */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                              <h5 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">INSTALLATION</h5>
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="text-gray-500">Installation Type:</span>
                                  <span className="ml-2 font-medium text-gray-900 dark:text-white">{formatInstallationType(item.installationType || item.installation_type)}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Track Mount:</span>
                                  <span className="ml-2 font-medium text-gray-900 dark:text-white">{formatTrackMount(item.trackMount || item.track_mount)}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">U-Channel:</span>
                                  <span className="ml-2 font-medium text-gray-900 dark:text-white">{formatUChannel(item.uChannel || item.u_channel)}</span>
                                </div>
                              </div>
                            </div>

                            {/* Options */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                              <h5 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">OPTIONS</h5>
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="text-gray-500">Motor Side:</span>
                                  <span className="ml-2 font-medium text-gray-900 dark:text-white capitalize">{item.motorSide || item.motor_side || 'Right'}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Brush Size:</span>
                                  <span className="ml-2 font-medium text-gray-900 dark:text-white">{formatBrushSize(item.brushSize || item.brush_size || orderInfo.brushSize)}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Biased Cut:</span>
                                  <span className="ml-2 font-medium text-gray-900 dark:text-white">
                                    {(item.biasedCut || item.biased_cut) ? `Yes (${item.biasedCutInches || item.biased_cut_inches || 0}")` : 'No'}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Window Panel:</span>
                                  <span className="ml-2 font-medium text-gray-900 dark:text-white">{(item.windowPanel || item.window_panel) ? 'Yes' : 'No'}</span>
                                </div>
                              </div>
                            </div>

                            {/* Screen Location */}
                            {(item.screenLocation || item.screen_location) && (
                              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                                <h5 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">SCREEN LOCATION</h5>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">{item.screenLocation || item.screen_location}</p>
                              </div>
                            )}

                            {/* Window Panel Details */}
                            <WindowPanelDetails item={item} />

                            {/* Price Breakdown */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                              <h5 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">PRICE</h5>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Unit Price:</span>
                                <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(item.unitPrice || item.unit_price || 0)}</span>
                              </div>
                              {((item.uChannelPrice || item.u_channel_price || 0) > 0) && (
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-500">U-Channel:</span>
                                  <span className="font-medium text-gray-900 dark:text-white">+{formatCurrency(item.uChannelPrice || item.u_channel_price)}</span>
                                </div>
                              )}
                              <div className="flex justify-between text-sm border-t border-gray-200 dark:border-gray-700 mt-2 pt-2">
                                <span className="text-gray-700 dark:text-gray-300 font-semibold">Total:</span>
                                <span className="font-bold text-gray-900 dark:text-keder-yellow">{formatCurrency(item.totalPrice || ((item.unitPrice || 0) + (item.uChannelPrice || 0)))}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Record Your Sale - for commission tracking */}
      {canRecordSellPrice() && (
        <div className={`rounded-lg shadow p-6 ${hasSellPriceRecorded() ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
          <div className="flex items-center gap-2 mb-4">
            <svg className={`w-5 h-5 ${hasSellPriceRecorded() ? 'text-green-600' : 'text-yellow-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className={`text-lg font-semibold ${hasSellPriceRecorded() ? 'text-green-800' : 'text-yellow-800'}`}>
              {hasSellPriceRecorded() ? 'Sale Recorded' : 'Record Your Sale'}
            </h2>
            <span className={`ml-2 text-xs px-2 py-1 rounded-full ${hasSellPriceRecorded() ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
              {hasSellPriceRecorded() ? 'Commission tracking enabled' : 'For commission tracking'}
            </span>
          </div>

          {hasSellPriceRecorded() ? (
            <div className="space-y-3">
              <dl className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-green-700">Your Sell Price</dt>
                  <dd className="font-semibold text-green-900 text-lg">
                    {formatCurrency((order as any).customerSellPrice || (order as any).customer_sell_price)}
                  </dd>
                </div>
                <div>
                  <dt className="text-green-700">Your Markup</dt>
                  <dd className="font-semibold text-green-900 text-lg">
                    {formatCurrency(((order as any).customerSellPrice || (order as any).customer_sell_price) - (invoiceBalance !== null ? invoiceBalance : (order.totalAmount || (order as any).total_amount || 0)))}
                  </dd>
                </div>
                {((order as any).dateSoldToCustomer || (order as any).date_sold_to_customer) && (
                  <div className="col-span-2">
                    <dt className="text-green-700">Date Sold</dt>
                    <dd className="font-medium text-green-900">
                      {new Date((order as any).dateSoldToCustomer || (order as any).date_sold_to_customer).toLocaleDateString()}
                    </dd>
                  </div>
                )}
              </dl>
              <p className="text-xs text-green-600 italic">
                This information is confidential and used only for your commission tracking.
              </p>
              <button
                onClick={() => {
                  setSellPriceForm({
                    customerSellPrice: String((order as any).customerSellPrice || (order as any).customer_sell_price || ''),
                    dateSoldToCustomer: (order as any).dateSoldToCustomer || (order as any).date_sold_to_customer || '',
                  });
                }}
                className="text-sm text-green-700 hover:text-green-900 underline"
              >
                Update sale info
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-yellow-700">
                Record your sell price to enable commission tracking. This is confidential and never shared. Defaults to your dealer cost — adjust if you marked it up.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-yellow-800 mb-1">
                    Your Sell Price
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      value={sellPriceForm.customerSellPrice}
                      onChange={(e) => setSellPriceForm(prev => ({ ...prev, customerSellPrice: e.target.value }))}
                      onWheel={(e) => e.currentTarget.blur()}
                      placeholder={String(order.totalAmount ?? (order as any).total_amount ?? '0.00')}
                      className="w-full pl-7 pr-3 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent bg-white"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  {(() => {
                    const parsed = parseFloat(sellPriceForm.customerSellPrice);
                    const total = order.totalAmount || (order as any).total_amount || 0;
                    if (!Number.isFinite(parsed) || parsed <= 0) {
                      return (
                        <p className="mt-1 text-xs text-yellow-700 italic">
                          Leave blank to record at your dealer cost ({formatCurrency(total)}) — no markup tracked.
                        </p>
                      );
                    }
                    const markup = parsed - total;
                    return (
                      <p className={`mt-1 text-sm ${markup > 0 ? 'text-green-600' : 'text-gray-500'}`}>
                        {markup > 0 ? `Markup: ${formatCurrency(markup)}` : 'No markup'}
                      </p>
                    );
                  })()}
                </div>
                <div>
                  <label className="block text-sm font-medium text-yellow-800 mb-1">
                    Date Sold to Customer (optional)
                  </label>
                  <input
                    type="date"
                    value={sellPriceForm.dateSoldToCustomer}
                    onChange={(e) => setSellPriceForm(prev => ({ ...prev, dateSoldToCustomer: e.target.value }))}
                    className="w-full px-3 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent bg-white"
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSaveSellPrice}
                  disabled={savingSellPrice}
                  className="px-4 py-2 bg-yellow-500 text-black font-semibold rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {savingSellPrice ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </button>
                {sellPriceSaved && (
                  <span className="text-green-600 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Saved!
                  </span>
                )}
              </div>
              <p className="text-xs text-yellow-600 italic">
                Your pricing is confidential. We use aggregate data for market analysis only.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Tracking info is now displayed inside the Shipping card above */}

      {/* Install Info — not shown for parts/sales kit orders */}
      {!isPartsOrder() && order.status === 'install_scheduled' && (order as any).installScheduledDate && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 text-blue-900">Scheduled Installation</h2>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-blue-700">Install Date</dt>
              <dd className="font-medium text-blue-900">
                {new Date((order as any).installScheduledDate).toLocaleDateString('en-US', {
                  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                })}
              </dd>
            </div>
            {(order as any).installScheduledTime && (
              <div className="flex justify-between">
                <dt className="text-blue-700">Time</dt>
                <dd className="font-medium text-blue-900">{(order as any).installScheduledTime}</dd>
              </div>
            )}
            {(order as any).installerName && (
              <div className="flex justify-between">
                <dt className="text-blue-700">Installer</dt>
                <dd className="font-medium text-blue-900">{(order as any).installerName}</dd>
              </div>
            )}
            {(order as any).installNotes && (
              <div className="flex justify-between">
                <dt className="text-blue-700">Notes</dt>
                <dd className="font-medium text-blue-900">{(order as any).installNotes}</dd>
              </div>
            )}
          </dl>
        </div>
      )}

      {/* Card fee consent → hosted Stripe checkout (Link/Apple/Google Pay + fee/debit-refund) */}
      <CardFeeConsentModal
        open={cardConsentOpen}
        amount={consentAmount}
        onClose={() => setCardConsentOpen(false)}
        onAgree={() => { setCardConsentOpen(false); proceedHostedCheckout('card', consentAmount, consentCredit); }}
      />

      {/* Authorize.net Multi-Tab Payment Modal */}
      {showCardModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="light-modal bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-lg font-semibold text-gray-900">Pay ${cardModalAmount.toFixed(2)}</h3>
              <button onClick={() => setShowCardModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <div className="mb-4 text-sm text-gray-500">
              <span className="font-medium text-gray-700">Order {order?.orderNumber || (order as any)?.order_number || ''}</span>
              {order?.sidemark && <span className="ml-2">— {order.sidemark}</span>}
              {items.length > 0 && <span className="ml-2">({items.length} screen{items.length !== 1 ? 's' : ''})</span>}
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 mb-4">
              {[
                { key: 'saved' as const, label: 'Saved', count: savedMethods.length },
                { key: 'card' as const, label: 'New Card' },
                { key: 'bank' as const, label: 'Bank Account' },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => { setCardModalTab(tab.key); setError(null); }}
                  className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${
                    cardModalTab === tab.key
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}{tab.count != null && tab.count > 0 ? ` (${tab.count})` : ''}
                </button>
              ))}
            </div>

            {/* Tab: Saved Methods */}
            {cardModalTab === 'saved' && (
              <div className="space-y-2 min-h-[120px]">
                {loadingSavedMethods ? (
                  <p className="text-sm text-gray-500 text-center py-8">Loading saved methods...</p>
                ) : savedMethods.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-500">No saved payment methods yet.</p>
                    <p className="text-xs text-gray-400 mt-1">Pay with a new card and check "Save for future" to save it.</p>
                  </div>
                ) : (
                  <>
                    {savedMethods.map(method => (
                      <label
                        key={method.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedSavedMethod === method.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="savedMethod"
                          checked={selectedSavedMethod === method.id}
                          onChange={() => setSelectedSavedMethod(method.id)}
                          className="accent-blue-500"
                        />
                        <div className="flex-1 min-w-0">
                          {method.paymentType === 'card' ? (
                            <span className="text-sm font-medium text-gray-900">
                              {method.cardBrand || 'Card'} ending in {method.cardLast4}
                              {method.cardExpMonth && method.cardExpYear && (
                                <span className="text-gray-400 ml-2 text-xs">exp {method.cardExpMonth}/{method.cardExpYear}</span>
                              )}
                            </span>
                          ) : (
                            <span className="text-sm font-medium text-gray-900">
                              {method.bankName || 'Bank'} {method.bankAccountType || ''} ending in {method.bankLast4}
                            </span>
                          )}
                          {method.label && <span className="text-xs text-gray-400 ml-2">{method.label}</span>}
                        </div>
                        <button
                          onClick={(e) => { e.preventDefault(); handleDeleteSavedMethod(method.id); }}
                          className="text-xs text-red-400 hover:text-red-600"
                        >
                          Remove
                        </button>
                      </label>
                    ))}
                  </>
                )}
              </div>
            )}

            {/* Tab: New Card */}
            {/* Card payments go through the fee-consent popup -> hosted Stripe checkout. */}

            {/* Tab: Bank Account (ACH via HelcimPay.js) */}
            {cardModalTab === 'bank' && (
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800 font-medium mb-2">Secure ACH Bank Payment</p>
                  <p className="text-xs text-blue-700">
                    Click the button below to open a secure payment form powered by Helcim.
                    You'll enter your bank account details directly on Helcim's secure form — your banking information is never stored on our servers.
                  </p>
                </div>

                {/* Address collection for ACH — shown when dealer has no address on file */}
                {!hasAddress && (
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-800 font-medium mb-3">Billing Address Required for ACH</p>
                    <div className="space-y-2">
                      <input
                        type="text"
                        placeholder="Street address (optional)"
                        value={achAddressForm.street}
                        onChange={(e) => setAchAddressForm(f => ({ ...f, street: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-900"
                      />
                      <div className="grid grid-cols-3 gap-2">
                        <input
                          type="text"
                          placeholder="City *"
                          value={achAddressForm.city}
                          onChange={(e) => setAchAddressForm(f => ({ ...f, city: e.target.value }))}
                          className="px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-900"
                        />
                        <input
                          type="text"
                          placeholder="State *"
                          value={achAddressForm.state}
                          onChange={(e) => setAchAddressForm(f => ({ ...f, state: e.target.value }))}
                          className="px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-900"
                        />
                        <input
                          type="text"
                          placeholder="ZIP *"
                          value={achAddressForm.zip}
                          onChange={(e) => setAchAddressForm(f => ({ ...f, zip: e.target.value }))}
                          className="px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-900"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-600 leading-relaxed">
                    By proceeding, I authorize International Shade Products Inc to debit my bank account for the amount of <strong>${cardModalAmount.toFixed(2)}</strong> on
                    or after <strong>{new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong> for
                    order <strong>{order?.orderNumber || (order as any)?.order_number || ''}</strong>.
                    This is a one-time electronic fund transfer. Funds may be withdrawn from your account as soon as the same day.
                  </p>
                </div>
              </div>
            )}

            {error && (
              <div className="mt-3 text-sm text-red-600 bg-red-50 p-2 rounded">{error}</div>
            )}

            {/* Action buttons per tab */}
            <div className="mt-4 flex gap-3">
              <button
                onClick={() => setShowCardModal(false)}
                disabled={cardProcessing}
                className="flex-1 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>

              {cardModalTab === 'saved' && (
                <button
                  onClick={handleChargeSavedMethod}
                  disabled={cardProcessing || !selectedSavedMethod}
                  className="flex-1 py-2.5 text-sm font-semibold text-white rounded-lg transition-colors disabled:opacity-50"
                  style={{ backgroundColor: '#16a34a' }}
                >
                  {cardProcessing ? 'Processing...' : `Pay $${cardModalAmount.toFixed(2)}`}
                </button>
              )}

              {/* Card tab uses StripeCardForm's own Continue/Confirm buttons */}

              {cardModalTab === 'bank' && (
                <button
                  onClick={handleBankAccountPayment}
                  disabled={cardProcessing}
                  className="flex-1 py-2.5 text-sm font-semibold text-white rounded-lg transition-colors disabled:opacity-50"
                  style={{ backgroundColor: '#16a34a' }}
                  title={helcimAchDiscountPercent > 0 ? `Save ${helcimAchDiscountPercent}% by paying with bank account` : undefined}
                >
                  {cardProcessing ? 'Opening secure form...' : (
                    helcimAchDiscountPercent > 0
                      ? <>Pay ${(cardModalAmount * (1 - helcimAchDiscountPercent / 100)).toFixed(2)} via ACH <span className="ml-1 text-xs opacity-75">-{helcimAchDiscountPercent}%</span></>
                      : `Pay $${cardModalAmount.toFixed(2)} via ACH`
                  )}
                </button>
              )}
            </div>

            <div className="flex justify-center mt-3">
              <a
                href={`https://verify.authorize.net/anetseal/?pid=825580f2-c649-4e78-bd1f-baf21f5cfd1c&rurl=${encodeURIComponent(window.location.href)}`}
                onClick={(e) => { e.preventDefault(); window.open(e.currentTarget.href, 'AuthorizeNetVerification', 'width=600,height=430,resizable=yes,scrollbars=yes,location=yes'); }}
                rel="noopener noreferrer"
                target="_blank"
              >
                <img src="https://verify.authorize.net/anetseal/images/secure90x72.gif" width="90" height="72" alt="Authorize.Net Merchant - Click to Verify" style={{ border: 0 }} />
              </a>
            </div>
            <p className="mt-2 text-xs text-gray-400 text-center">
              Card payments processed by Authorize.net. Bank/ACH payments processed by Helcim. Your details are never stored on our servers.
            </p>
          </div>
        </div>
      )}

      {/* Schedule Install Modal */}
      {showScheduleInstallModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-900">
              {order.status === 'install_scheduled' ? 'Reschedule Installation' : 'Schedule Installation'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Install Date *</label>
                <input
                  type="date"
                  value={installForm.installDate}
                  onChange={(e) => setInstallForm({ ...installForm, installDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time (optional)</label>
                <input
                  type="time"
                  value={installForm.installTime}
                  onChange={(e) => setInstallForm({ ...installForm, installTime: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Installer Name (optional)</label>
                <input
                  type="text"
                  value={installForm.installerName}
                  onChange={(e) => setInstallForm({ ...installForm, installerName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Name of installer or crew"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <textarea
                  value={installForm.notes}
                  onChange={(e) => setInstallForm({ ...installForm, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Access instructions, customer contact info, etc."
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowScheduleInstallModal(false);
                  setInstallForm({ installDate: '', installTime: '', notes: '', installerName: '' });
                }}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={order.status === 'install_scheduled' ? handleRescheduleInstall : handleScheduleInstall}
                disabled={submitting || !installForm.installDate}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? 'Saving...' : order.status === 'install_scheduled' ? 'Reschedule' : 'Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mark Installed Modal */}
      {showMarkInstalledModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-900">Confirm Installation Complete</h3>
            <p className="text-gray-600 mb-4">
              Mark this order as installed? This will allow you to register the warranty.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Installer Name (optional)</label>
                <input
                  type="text"
                  value={installForm.installerName}
                  onChange={(e) => setInstallForm({ ...installForm, installerName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Name of person who installed"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowMarkInstalledModal(false);
                  setInstallForm({ installDate: '', installTime: '', notes: '', installerName: '' });
                }}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkInstalled}
                disabled={submitting}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {submitting ? 'Saving...' : 'Mark Installed'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetailPage;
